import { createI18n } from 'vue-i18n'
import { resolveInitialLocale, readUrlLang, readBrowserLangs } from './utils/locale'

const messages = {
  en: {
    nav: {
      home: 'Home',
      offers: 'Offers',
      services: 'Booking',
      atmosphere: 'Atmosphere',
      location: 'Location',
      massages: 'Massages',
      visitUs: 'Visit us',
      faq: 'FAQ'
    },
    services: {
      title: 'Check when it’s available — click here!',
      duration: 'Duration',
      startingFrom: 'Starting from',
      minutes: 'Minutes',
      bookNow: 'Book Now',
      loading: 'Loading services...',
      popular: 'Popular',
      pairBadge: 'For 2',
      more: 'More',
      less: 'Less'
    },
    atmosphere: {
      title: 'Atmosphere',
      subtitle: 'Experience our relaxing environment'
    },
    location: {
      title: 'Location',
      subtitle: 'Find us on the map'
    },
    modal: {
      title: 'Choose Your Massage Type',
      type1: 'Traditional Thai Massage',
      type2: 'Thai Oil Massage',
      type3: 'Thai Aroma Massage',
      close: 'Close',
      dateTimeTitle: 'Select Date & Time',
      selectDate: 'Select Date',
      selectTime: 'Select Time',
      firstName: 'First Name',
      lastName: 'Last Name',
      phone: 'Phone Number',
      email: 'Email Address',
      emailFollowUpNotice: '{studio} may email you after your visit to ask for feedback and offer new bookings. Unsubscribe any time.',
      persons: 'Number of Persons',
      back: 'Back',
      confirm: 'Book Now',
      termsAcceptance: 'I have read and agree to the Terms & Conditions and Right of Withdrawal.',
      totalPrice: 'Total Price',
      sum: 'Sum',
      total: 'Total',
      specialWish: 'Special Wishes / Notes',
      specialWishPlaceholder: 'Any special requests or notes for your visit... (optional)',
      therapist: 'Therapist',
      therapist1: 'Therapist 1',
      therapist2: 'Therapist 2',
      therapistNoPreference: 'No preference',
      therapistUnavailable: 'This therapist is already booked at the selected time. Please choose a different time or therapist.',
      sameDayLeadTime: "Same-day bookings need at least {minutes} minutes notice. Please choose a later time.",
      therapistTimeFiltered: 'Your previously selected time is not available with this therapist. Please choose an available time slot from the grid above.',
      maxPersonsHint: 'If you wish to book for more than 2 persons, please make a separate booking.',
      paymentTitle: 'Choose Payment Method',
      paymentMethod: 'How would you like to pay?',
      payFull: 'Pay in full ({amount})',
      payDeposit: 'Pay deposit now ({amount}), rest at venue',
      payCash: 'Pay cash at venue',
      cardDetails: 'Card Details',
      confirmAndPay: 'Confirm & Pay {amount}',
      paymentProcessing: 'Processing payment...',
      paymentError: 'Payment failed. Please try again.',
      securePayment: 'Secure payment powered by Stripe'
    },
    notification: {
      close: 'Close',
      bookingConfirmed: 'Booking Confirmed!',
      confirmationEmail: 'Booking confirmation will be sent to your email',
      selectDateFirst: 'Please select a date first to see available times',
      loadingTimeSlots: 'Loading available times...',
      noAvailableTimes: 'No available times for this date. Please select another date.',
      bookingFailed: 'Booking Failed',
      checkInformation: 'Please check your information and try again',
      networkError: 'Network Error',
      checkConnection: 'Please check your connection and try again',
      date: 'Date',
      time: 'Time',
      massageType: 'Massage Type',
      duration: 'Duration',
      amountPaid: 'Amount Paid',
      remainingAtVenue: 'Remaining at venue',
      refundPolicy: 'Refund Policy',
      cancellationPolicy: 'Cancellation policy',
      noRefunds: 'No refunds',
      refundWindow: 'Cancellations accepted up to {hours} hours before your appointment.',
      cancellationFee: 'A cancellation fee of €5 applies.',
      lateCancellationPolicy: 'If something comes up and you can\'t make your appointment, please let us know at least {hours} hours in advance, by phone only.',
      timeRemaining: 'Starts in'
    },
    hero: {
      voucherBtn: 'Buy Voucher',
      openUntil: 'Open today until {time}',
      opensAt: 'Opens today at {time}',
      closedToday: 'Closed today'
    },
    cta: {
      fromPrice: 'from {price} · {minutes} min'
    },
    trust: {
      sameDay: 'Same-day appointments available',
      cancelFree: 'Free cancellation up to {hours} h before',
      // NOTE: this slot may state WHEN payment happens and THAT online card payment
      // exists - never HOW payment is taken at the studio. Nothing in the system
      // records a studio's accepted on-site methods, so any such claim is unfounded.
      payOnline: 'Secure online payment',
      payAtVenue: 'Pay at your appointment'
    },
    footer: {
      impressum: 'Impressum',
      dataProtection: 'Data Protection',
      terms: 'Terms & Conditions',
      address: 'Address',
      openingHours: 'Opening Hours',
      email: 'Email',
      rights: 'All rights reserved.',
      poweredBy: 'powered by'
    },
    common: {
      closed: 'Closed',
      language: 'Language',
      expand: 'Expand',
      collapse: 'Collapse',
      previousImage: 'Previous image',
      nextImage: 'Next image',
      imageViewer: 'Image viewer',
      loading: 'Loading…',
      min: 'min'
    },
    validation: {
      phoneInvalid: 'Phone number must start with + or a digit.',
      emailInvalid: 'Please enter a valid email address.',
      notesInvalid: 'The characters @ \' $ # are not allowed.',
      required: 'This field is required.'
    },
    booking: {
      processing: 'Processing…',
      calculating: 'Calculating…',
      datePlaceholder: 'Select a date',
      loadError: 'Could not load the latest data. Please try again.',
      slotsLoadError: 'Could not load available times. Please try again.',
      typeUnavailableOnDay: '{type} isn\'t available on this day. Please choose another day or massage.',
      timeNoLongerAvailable: 'This time is no longer available. Please choose another time.',
      chooseType: 'Choose your massage',
      chooseTime: 'Choose a time',
      yourDetails: 'Your details',
      confirmAndPay: 'Confirm and pay',
      confirmBooking: 'Confirm booking',
      stepOf: 'Step {current} of {total}',
      change: 'Change',
      continue: 'Continue',
      moreDates: 'More dates',
      fewerDates: 'Fewer dates',
      morning: 'Morning',
      afternoon: 'Afternoon',
      evening: 'Evening',
      slotsLeft: '{n} left',
      anyTherapist: 'Any therapist',
      confirmAmount: 'Confirm · {amount}',
      payAmount: 'Pay {amount}',
      retry: 'Try again',
      done: 'Done',
      duration: 'Duration',
      selectDuration: 'Select duration',
      durationTimeFiltered: 'Your chosen time is not available for this duration. Please pick another time.',
      pairMassageHint: 'Pair massage – booked for 2 persons',
      priceForTwo: 'for 2 persons',
      priceBase: 'Base price',
      priceSurcharge: 'Surcharge',
      priceDiscount: 'Discount',
      priceDetails: 'Details',
      priceBreakdown: 'Price breakdown',
      prefilledHint: 'We\'ve filled in your details from your last booking.',
      prefilledClear: 'Not you? Clear'
    },
    visit: {
      loadingMap: 'Loading map…',
      title: 'Visit us',
      openInMaps: 'Open in Google Maps',
      call: 'Call',
      today: 'Today'
    },
    days: {
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday'
    },
    offers: {
      title: 'Our Massages',
      loading: 'Loading offers...',
      popular: 'Popular',
      bookFor: 'Book for:',
      selectDuration: 'Select duration',
      priceAdjustment: 'Price adjustment',
      noDescription: 'No description available.',
      traditional: {
        title: 'Traditional Thai Massage',
        description: 'A deep, pressure-based massage that stretches and loosens muscles using hands, elbows, and feet. Often done on a mat fully clothed.'
      },
      oil: {
        title: 'Thai Oil Massage',
        description: 'Combines gentle Thai techniques with smooth oil strokes to relax muscles, improve circulation, and nourish the skin.'
      },
      aroma: {
        title: 'Thai Aroma Massage',
        description: 'Uses aromatic essential oils along with Thai massage techniques to relax the body and mind while enhancing mood and wellness.'
      }
    },
    faq: {
      title: 'Thai Massage – Frequently Asked Questions',
      showAll: 'Show all {count} questions',
      showLess: 'Show fewer',
      cat1: 'What is Thai Massage?',
      cat2: 'Why Thai Massage is Different',
      cat3: 'What to Expect',
      cat4: 'Why Customers Choose Thai Massage',
      cat5: 'Before Your Visit',
      cat6: 'Booking & Experience',
      cat7: 'Results & Benefits',
      q1: 'What is Thai massage?',
      a1: 'Thai massage is a traditional full-body therapy that combines stretching, acupressure, and rhythmic movements. It helps release tension, improve flexibility, and restore energy balance — making it both relaxing and revitalizing.',
      q2: 'How is Thai massage different from oil or spa massage?',
      a2: 'Unlike oil or spa massages that focus mainly on muscle relaxation, Thai massage is more active and movement-based. It is performed fully clothed on a comfortable mat and includes assisted stretching, similar to yoga. This makes it ideal for improving flexibility, posture, and long-term body wellbeing — not just short-term relaxation.',
      q3: 'How is Thai massage different from deep tissue massage?',
      a3: 'Deep tissue massage focuses on applying strong pressure to specific tight muscles. Thai massage works on the entire body through stretching, pressure points, and movement. It provides a full-body reset rather than targeting only isolated pain areas.',
      q4: 'How is Thai massage different from sports massage?',
      a4: 'Sports massage is designed for athletes and injury recovery. Thai massage is suitable for everyone and focuses on reducing stiffness, improving mobility, and relieving everyday tension caused by work, stress, or sitting for long hours.',
      q5: 'What does Thai massage feel like?',
      a5: 'You will experience a combination of gentle stretching, firm pressure, and flowing movements. Many guests describe it as deeply relaxing yet energizing at the same time.',
      q6: 'Is Thai massage painful?',
      a6: 'No — it should never be painful. The pressure and stretching are always adjusted to your comfort level. You can request lighter or stronger intensity at any time.',
      q7: 'Will I feel better after one session?',
      a7: 'Most guests feel immediate relief from tension, improved flexibility, and a lighter, more relaxed body after just one session.',
      q8: 'Why should I choose Thai massage instead of a normal relaxation massage?',
      a8: "Thai massage goes beyond relaxation. It helps release deep stiffness, improves mobility, and restores energy flow throughout the body. It's especially beneficial if you sit often, feel tight muscles, or want a full-body reset.",
      q9: 'Do I need to prepare anything before my appointment?',
      a9: 'No preparation is needed. Just arrive a few minutes early and wear comfortable clothing.',
      q10: 'Do I need prior experience?',
      a10: 'Not at all. Thai massage is suitable for beginners and first-time visitors.',
      q11: 'How can I book an appointment?',
      a11: 'You can book online in just a few seconds or call the shop directly. Walk-ins are also welcome depending on availability.',
      q12: 'Can I choose pressure level?',
      a12: 'Yes. You can choose light, medium, or strong pressure based on your preference.',
      q13: 'Can I come with a friend or partner?',
      a13: 'Yes, many locations offer couple or side-by-side treatments depending on room availability.',
      q14: 'What are the main benefits of Thai massage?',
      a14: 'Thai massage can help reduce stress, improve flexibility, increase circulation, and relieve muscle stiffness caused by daily life or work.',
      q15: 'How often should I get a Thai massage?',
      a15: 'Many clients book once per week or every two weeks to maintain flexibility, reduce stress, and prevent tension buildup.',
      ctaTitle: 'Ready to feel better?',
      ctaBody: 'Book your Thai massage now and enjoy a full-body reset that leaves you relaxed, flexible, and recharged.',
      ctaBullet1: 'Same-day appointments often available',
      ctaBullet2: 'Walk-ins welcome (subject to availability)',
    },
    cancellation: {
      title: 'Cancel Appointment',
      loading: 'Loading booking details...',
      error: 'Unable to Load Booking',
      close: 'Close',
      bookingDetails: 'Booking Details',
      bookingId: 'Booking ID',
      customer: 'Customer',
      service: 'Service',
      massageType: 'Massage Type',
      date: 'Date',
      time: 'Time',
      persons: 'Persons',
      totalPrice: 'Total Price',
      warningTitle: 'Short Notice Cancellation',
      warningMessage: 'Your appointment is in less than {hours} hours. Late cancellations may be subject to fees.',
      hours: 'hours',
      confirmTitle: 'Confirm Cancellation',
      reasonLabel: 'Reason for cancellation (optional)',
      reasonPlaceholder: 'Please let us know why you need to cancel...',
      processing: 'Processing...',
      confirmCancel: 'Yes, Cancel Appointment',
      keepBooking: 'Keep My Booking',
      successTitle: 'Appointment Cancelled',
      successMessage: 'Your appointment has been successfully cancelled.',
      invalidLink: 'Invalid cancellation link',
      cannotCancel: 'This booking cannot be cancelled',
      confirmPrompt: 'Are you sure you want to cancel this appointment? This action cannot be undone.',
      errorMessage: 'Failed to cancel appointment. Please try again or contact us directly.',
      refundNoticeTitle: 'Refund will be issued',
      refundNoticeBody: 'A refund of {amount} will be processed (€5 cancellation fee applied).',
      outsideWindowTitle: 'Cancellation window has passed',
      outsideWindowBody: 'Please contact the studio directly to claim your refund.',
      contactStudioInstruction: 'To request a refund, please contact the studio directly.',
      successWithRefund: 'Your appointment has been cancelled. A refund of {amount} will be processed within 5–10 business days.',
      successContactStudio: 'Your appointment has been cancelled. Please contact the studio directly to arrange your refund.',
      verifyEmail: 'Your email address',
      verifyEmailPlaceholder: 'Enter the email used when booking',
      lateBlockedTitle: 'Online cancellation no longer possible',
      lateBlockedText: 'Online cancellation isn\'t possible within {hours} hours of your appointment. Please call the studio to cancel.',
      lateBlockedNoPhone: 'Online cancellation isn\'t possible within {hours} hours of your appointment. Please contact the studio directly to cancel.',
      hoursRemaining: '{hours} h remaining',
      callStudio: 'Call the studio'
    },
    announcement: {
      close: 'Close',
      previous: 'Previous',
      next: 'Next',
      noshow: "Don't show again today"
    },
    offline: {
      title: 'Temporarily closed',
      body: 'Online booking is currently unavailable. Please check back later.',
      bookingRefused: 'This studio is temporarily not taking online bookings. Please reload the page.'
    },
    loadLocation: {
      title: 'Location',
      subtitle: 'Find us on the map',
      loadMap: 'Click to load Google Maps',
      mapPrivacyNotice: 'Loading the map will transmit data to Google'
    },
    rating: {
      title: 'How did booking go?',
      hint: 'Optional. It helps us make booking easier.',
      starLabel: '{n} of 5 stars',
      improveTitle: 'What could be better?',
      reasonAppointment: 'Finding a time',
      reasonService: 'Picking a service',
      reasonTherapist: 'Picking a therapist',
      reasonConfusing: 'The page was confusing',
      reasonSlow: 'It took too long',
      reasonOther: 'Something else',
      commentPlaceholder: 'Tell us a bit more (optional)',
      submit: 'Send',
      skip: 'Skip',
      thanks: 'Thank you, that really helps.'
    }
  },
  de: {
    nav: {
      home: 'Startseite',
      offers: 'Angebote',
      services: 'Buchung',
      atmosphere: 'Atmosphäre',
      location: 'Standort',
      massages: 'Massagen',
      visitUs: 'Besuchen Sie uns',
      faq: 'FAQ'
    },
    services: {
      title: 'Verfügbarkeit prüfen – jetzt klicken!',
      duration: 'Dauer',
      startingFrom: 'Ab',
      minutes: 'Minuten',
      bookNow: 'Jetzt buchen',
      loading: 'Lade Dienstleistungen...',
      popular: 'Beliebt',
      pairBadge: 'Zu zweit',
      more: 'Mehr',
      less: 'Weniger'
    },
    atmosphere: {
      title: 'Atmosphäre',
      subtitle: 'Erleben Sie unsere entspannende Umgebung'
    },
    location: {
      title: 'Standort',
      subtitle: 'Finden Sie uns auf der Karte'
    },
    modal: {
      title: 'Wählen Sie Ihre Massage-Art',
      type1: 'Traditionelle Thai-Massage',
      type2: 'Thaimassage mit Öl',
      type3: 'Thai Aroma-Massage',
      close: 'Schließen',
      dateTimeTitle: 'Datum & Zeit wählen',
      selectDate: 'Datum wählen',
      selectTime: 'Zeit wählen',
      firstName: 'Vorname',
      lastName: 'Nachname',
      phone: 'Telefonnummer',
      email: 'E-Mail-Adresse',
      emailFollowUpNotice: '{studio} kann Ihnen nach Ihrem Besuch eine E-Mail senden, um Sie um Feedback zu bitten und Ihnen neue Termine anzubieten. Sie können sich jederzeit abmelden.',
      persons: 'Personenzahl',
      back: 'Zurück',
      confirm: 'Verbindlich buchen',
      termsAcceptance: 'Ich habe die AGB und die Widerrufsbelehrung gelesen und stimme ihnen zu.',
      totalPrice: 'Gesamtpreis',
      sum: 'Summe',
      total: 'Gesamt',
      specialWish: 'Besondere Wünsche / Hinweise',
      specialWishPlaceholder: 'Besondere Wünsche oder Hinweise für Ihren Besuch... (optional)',
      therapist: 'Therapeutin',
      therapist1: 'Therapeutin 1',
      therapist2: 'Therapeutin 2',
      therapistNoPreference: 'Keine Präferenz',
      therapistUnavailable: 'Diese Therapeutin ist zur gewählten Zeit bereits gebucht. Bitte wählen Sie eine andere Zeit oder Therapeutin.',
      sameDayLeadTime: "Buchungen für heute sind nur mit mindestens {minutes} Minuten Vorlauf möglich. Bitte wählen Sie eine spätere Uhrzeit.",
      therapistTimeFiltered: 'Ihre zuvor gewählte Uhrzeit ist bei diesem Therapeuten nicht verfügbar. Bitte wählen Sie eine verfügbare Zeit aus dem Raster oben.',
      maxPersonsHint: 'Wenn Sie für mehr als 2 Personen buchen möchten, erstellen Sie bitte eine separate Buchung.',
      paymentTitle: 'Zahlungsmethode wählen',
      paymentMethod: 'Wie möchten Sie bezahlen?',
      payFull: 'Vollständig bezahlen ({amount})',
      payDeposit: 'Anzahlung jetzt ({amount}), Rest vor Ort',
      payCash: 'Bar vor Ort bezahlen',
      cardDetails: 'Kartendaten',
      confirmAndPay: 'Bestätigen & bezahlen {amount}',
      paymentProcessing: 'Zahlung wird verarbeitet...',
      paymentError: 'Zahlung fehlgeschlagen. Bitte erneut versuchen.',
      securePayment: 'Sichere Zahlung powered by Stripe'
    },
    notification: {
      close: 'Schließen',
      bookingConfirmed: 'Buchung bestätigt!',
      confirmationEmail: 'Die Buchungsbestätigung wird an Ihre E-Mail gesendet',
      selectDateFirst: 'Bitte wählen Sie zuerst ein Datum, um verfügbare Zeiten zu sehen',
      loadingTimeSlots: 'Verfügbare Zeiten werden geladen...',
      noAvailableTimes: 'Keine verfügbaren Zeiten für dieses Datum. Bitte wählen Sie ein anderes Datum.',
      bookingFailed: 'Buchung fehlgeschlagen',
      checkInformation: 'Bitte überprüfen Sie Ihre Angaben und versuchen Sie es erneut',
      networkError: 'Netzwerkfehler',
      checkConnection: 'Bitte überprüfen Sie Ihre Verbindung und versuchen Sie es erneut',
      date: 'Datum',
      time: 'Uhrzeit',
      massageType: 'Massage-Art',
      duration: 'Dauer',
      amountPaid: 'Bezahlter Betrag',
      remainingAtVenue: 'Rest vor Ort',
      refundPolicy: 'Rückerstattungsrichtlinie',
      cancellationPolicy: 'Stornierungsbedingungen',
      noRefunds: 'Keine Rückerstattungen',
      refundWindow: 'Stornierung möglich bis {hours} Stunden vor Ihrem Termin.',
      cancellationFee: 'Es fällt eine Stornierungsgebühr von €5 an.',
      lateCancellationPolicy: 'Sollte mal etwas dazwischen kommen und Sie Ihren Termin nicht wahrnehmen können, bitten wir Sie uns mindestens {hours} Stunden ausschließlich per Telefon Bescheid zu geben.',
      timeRemaining: 'Beginn in'
    },
    hero: {
      voucherBtn: 'Gutschein kaufen',
      openUntil: 'Heute geöffnet bis {time}',
      opensAt: 'Öffnet heute um {time}',
      closedToday: 'Heute geschlossen'
    },
    cta: {
      fromPrice: 'ab {price} · {minutes} Min.'
    },
    trust: {
      sameDay: 'Termine am selben Tag möglich',
      cancelFree: 'Kostenlose Stornierung bis {hours} Std. vorher',
      payOnline: 'Sichere Online-Zahlung',
      payAtVenue: 'Zahlung erst beim Termin'
    },
    footer: {
      impressum: 'Impressum',
      dataProtection: 'Datenschutz',
      terms: 'AGB',
      address: 'Adresse',
      openingHours: 'Öffnungszeiten',
      email: 'E-Mail',
      rights: 'Alle Rechte vorbehalten.',
      poweredBy: 'powered by'
    },
    common: {
      closed: 'Geschlossen',
      language: 'Sprache',
      expand: 'Aufklappen',
      collapse: 'Zuklappen',
      previousImage: 'Vorheriges Bild',
      nextImage: 'Nächstes Bild',
      imageViewer: 'Bildansicht',
      loading: 'Wird geladen…',
      min: 'Min.'
    },
    validation: {
      phoneInvalid: 'Die Telefonnummer muss mit + oder einer Ziffer beginnen.',
      emailInvalid: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
      notesInvalid: 'Die Zeichen @ \' $ # sind nicht erlaubt.',
      required: 'Dieses Feld ist erforderlich.'
    },
    booking: {
      processing: 'Wird verarbeitet…',
      calculating: 'Wird berechnet…',
      datePlaceholder: 'Datum wählen',
      loadError: 'Die aktuellen Daten konnten nicht geladen werden. Bitte versuchen Sie es erneut.',
      slotsLoadError: 'Die verfügbaren Zeiten konnten nicht geladen werden. Bitte versuchen Sie es erneut.',
      typeUnavailableOnDay: '{type} ist an diesem Tag leider nicht verfügbar. Bitte wählen Sie einen anderen Tag oder eine andere Massage.',
      timeNoLongerAvailable: 'Diese Uhrzeit ist leider nicht mehr verfügbar. Bitte wählen Sie eine andere Zeit.',
      chooseType: 'Massage auswählen',
      chooseTime: 'Zeit auswählen',
      yourDetails: 'Ihre Daten',
      confirmAndPay: 'Bestätigen und bezahlen',
      confirmBooking: 'Buchung bestätigen',
      stepOf: 'Schritt {current} von {total}',
      change: 'Ändern',
      continue: 'Weiter',
      moreDates: 'Weitere Termine',
      fewerDates: 'Weniger Termine',
      morning: 'Vormittag',
      afternoon: 'Nachmittag',
      evening: 'Abend',
      slotsLeft: 'Noch {n} frei',
      anyTherapist: 'Beliebige Therapeutin',
      confirmAmount: 'Bestätigen · {amount}',
      payAmount: '{amount} bezahlen',
      retry: 'Erneut versuchen',
      done: 'Fertig',
      duration: 'Dauer',
      selectDuration: 'Dauer wählen',
      durationTimeFiltered: 'Die gewählte Uhrzeit ist für diese Dauer nicht verfügbar. Bitte eine andere Uhrzeit wählen.',
      pairMassageHint: 'Paarmassage – Buchung für 2 Personen',
      priceForTwo: 'für 2 Personen',
      priceBase: 'Grundpreis',
      priceSurcharge: 'Zuschlag',
      priceDiscount: 'Rabatt',
      priceDetails: 'Details',
      priceBreakdown: 'Preisaufstellung',
      prefilledHint: 'Wir haben Ihre Angaben aus Ihrer letzten Buchung übernommen.',
      prefilledClear: 'Nicht Sie? Löschen'
    },
    visit: {
      loadingMap: 'Karte wird geladen…',
      title: 'Besuchen Sie uns',
      openInMaps: 'In Google Maps öffnen',
      call: 'Anrufen',
      today: 'Heute'
    },
    days: {
      monday: 'Montag',
      tuesday: 'Dienstag',
      wednesday: 'Mittwoch',
      thursday: 'Donnerstag',
      friday: 'Freitag',
      saturday: 'Samstag',
      sunday: 'Sonntag'
    },
    offers: {
      title: 'Unsere Massagen',
      loading: 'Lade Angebote...',
      popular: 'Beliebt',
      bookFor: 'Buchen für:',
      selectDuration: 'Dauer auswählen',
      priceAdjustment: 'Preisanpassung',
      noDescription: 'Keine Beschreibung verfügbar.',
      traditional: {
        title: 'Traditionelle Thai-Massage',
        description: 'Eine tiefgehende, druckbasierte Massage, die Muskeln mit Händen, Ellbogen und Füßen dehnt und lockert. Oft auf einer Matte vollständig bekleidet durchgeführt.'
      },
      oil: {
        title: 'Thaimassage mit Öl',
        description: 'Kombiniert sanfte Thai-Techniken mit fließenden Ölstreichungen, um Muskeln zu entspannen, die Durchblutung zu verbessern und die Haut zu pflegen.'
      },
      aroma: {
        title: 'Thai Aroma-Massage',
        description: 'Verwendet aromatische ätherische Öle zusammen mit Thai-Massagetechniken, um Körper und Geist zu entspannen und gleichzeitig Stimmung und Wohlbefinden zu steigern.'
      }
    },
    faq: {
      title: 'Thaimassage – Häufig gestellte Fragen',
      showAll: 'Alle {count} Fragen anzeigen',
      showLess: 'Weniger anzeigen',
      cat1: 'Was ist eine Thaimassage?',
      cat2: 'Warum Thaimassage anders ist',
      cat3: 'Was Sie erwartet',
      cat4: 'Warum Kunden Thaimassage wählen',
      cat5: 'Vor Ihrem Besuch',
      cat6: 'Buchung & Erlebnis',
      cat7: 'Ergebnisse & Vorteile',
      q1: 'Was ist eine Thaimassage?',
      a1: 'Die Thaimassage ist eine traditionelle Ganzkörpertherapie, die Dehnübungen, Akupressur und rhythmische Bewegungen kombiniert. Sie hilft, Verspannungen zu lösen, die Flexibilität zu verbessern und das Energiegleichgewicht wiederherzustellen – sowohl entspannend als auch belebend.',
      q2: 'Wie unterscheidet sich Thaimassage von einer Öl- oder Spa-Massage?',
      a2: 'Im Gegensatz zu Öl- oder Spa-Massagen, die sich hauptsächlich auf Muskelentspannung konzentrieren, ist die Thaimassage aktiver und bewegungsbasiert. Sie wird vollständig bekleidet auf einer bequemen Matte durchgeführt und beinhaltet assistierte Dehnübungen, ähnlich wie Yoga. Das macht sie ideal für die Verbesserung von Flexibilität, Haltung und langfristigem Körperwohlbefinden.',
      q3: 'Wie unterscheidet sich Thaimassage von einer Tiefengewebsmassage?',
      a3: 'Die Tiefengewebsmassage konzentriert sich auf starken Druck auf bestimmte verspannte Muskeln. Die Thaimassage arbeitet durch Dehnung, Druckpunkte und Bewegung am gesamten Körper und bietet einen kompletten Körper-Reset statt nur isolierte Schmerzbereiche zu behandeln.',
      q4: 'Wie unterscheidet sich Thaimassage von einer Sportmassage?',
      a4: 'Sportmassagen sind für Athleten und Verletzungsrehabilitation gedacht. Thaimassage ist für alle geeignet und konzentriert sich auf die Reduzierung von Steifheit, die Verbesserung der Mobilität und die Linderung alltäglicher Spannungen durch Arbeit, Stress oder langes Sitzen.',
      q5: 'Wie fühlt sich eine Thaimassage an?',
      a5: 'Sie werden eine Kombination aus sanften Dehnübungen, festem Druck und fließenden Bewegungen erleben. Viele Gäste beschreiben sie als tiefenentspannend und gleichzeitig belebend.',
      q6: 'Ist Thaimassage schmerzhaft?',
      a6: 'Nein – es sollte niemals schmerzhaft sein. Druck und Dehnung werden immer Ihrem Komfortniveau angepasst. Sie können jederzeit eine leichtere oder stärkere Intensität anfordern.',
      q7: 'Werde ich nach einer Sitzung besser fühlen?',
      a7: 'Die meisten Gäste spüren nach nur einer Sitzung sofortige Erleichterung von Verspannungen, verbesserte Flexibilität und einen leichteren, entspannteren Körper.',
      q8: 'Warum sollte ich Thaimassage statt einer normalen Entspannungsmassage wählen?',
      a8: 'Thaimassage geht über Entspannung hinaus. Sie hilft, tiefe Steifheit zu lösen, die Mobilität zu verbessern und den Energiefluss im ganzen Körper wiederherzustellen. Besonders vorteilhaft, wenn Sie oft sitzen, verspannte Muskeln haben oder einen kompletten Körper-Reset wünschen.',
      q9: 'Muss ich mich vor dem Termin vorbereiten?',
      a9: 'Keine Vorbereitung notwendig. Kommen Sie einfach ein paar Minuten früher an und tragen Sie bequeme Kleidung.',
      q10: 'Benötige ich Vorerfahrung?',
      a10: 'Überhaupt nicht. Thaimassage ist für Anfänger und Erstbesucher geeignet.',
      q11: 'Wie kann ich einen Termin buchen?',
      a11: 'Sie können online in wenigen Sekunden buchen oder direkt im Geschäft anrufen. Walk-ins sind je nach Verfügbarkeit ebenfalls willkommen.',
      q12: 'Kann ich den Druckgrad wählen?',
      a12: 'Ja. Sie können leichten, mittleren oder starken Druck nach Ihrer Präferenz wählen.',
      q13: 'Kann ich mit einem Freund oder Partner kommen?',
      a13: 'Ja, viele Standorte bieten Paar- oder Nebeneinander-Behandlungen je nach Raumverfügbarkeit an.',
      q14: 'Was sind die Hauptvorteile von Thaimassage?',
      a14: 'Thaimassage kann helfen, Stress zu reduzieren, Flexibilität zu verbessern, die Durchblutung zu fördern und Muskelsteifheit durch den Alltag oder die Arbeit zu lindern.',
      q15: 'Wie oft sollte ich eine Thaimassage bekommen?',
      a15: 'Viele Kunden buchen einmal pro Woche oder alle zwei Wochen, um Flexibilität aufrechtzuerhalten, Stress zu reduzieren und Verspannungen vorzubeugen.',
      ctaTitle: 'Bereit, sich besser zu fühlen?',
      ctaBody: 'Buchen Sie jetzt Ihre Thaimassage und genießen Sie einen kompletten Körper-Reset, der Sie entspannt, flexibel und erholt zurücklässt.',
      ctaBullet1: 'Tagestermine oft verfügbar',
      ctaBullet2: 'Walk-ins willkommen (je nach Verfügbarkeit)',
    },
    cancellation: {
      title: 'Termin absagen',
      loading: 'Lade Buchungsdetails...',
      error: 'Buchung kann nicht geladen werden',
      close: 'Schließen',
      bookingDetails: 'Buchungsdetails',
      bookingId: 'Buchungs-ID',
      customer: 'Kunde',
      service: 'Service',
      massageType: 'Massage-Art',
      date: 'Datum',
      time: 'Zeit',
      persons: 'Personen',
      totalPrice: 'Gesamtpreis',
      warningTitle: 'Kurzfristige Stornierung',
      warningMessage: 'Ihr Termin ist in weniger als {hours} Stunden. Kurzfristige Stornierungen können gebührenpflichtig sein.',
      hours: 'Stunden',
      confirmTitle: 'Stornierung bestätigen',
      reasonLabel: 'Grund für Stornierung (optional)',
      reasonPlaceholder: 'Bitte teilen Sie uns mit, warum Sie stornieren möchten...',
      processing: 'Verarbeitung...',
      confirmCancel: 'Ja, Termin stornieren',
      keepBooking: 'Buchung beibehalten',
      successTitle: 'Termin storniert',
      successMessage: 'Ihr Termin wurde erfolgreich storniert.',
      invalidLink: 'Ungültiger Stornierungslink',
      cannotCancel: 'Diese Buchung kann nicht storniert werden',
      confirmPrompt: 'Sind Sie sicher, dass Sie diesen Termin stornieren möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      errorMessage: 'Termin konnte nicht storniert werden. Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt.',
      refundNoticeTitle: 'Rückerstattung wird ausgestellt',
      refundNoticeBody: 'Eine Rückerstattung von {amount} wird bearbeitet (€5 Stornierungsgebühr wird abgezogen).',
      outsideWindowTitle: 'Stornierungsfrist abgelaufen',
      outsideWindowBody: 'Bitte kontaktieren Sie das Studio direkt, um Ihre Rückerstattung zu beantragen.',
      contactStudioInstruction: 'Bitte kontaktieren Sie das Studio direkt, um eine Rückerstattung zu beantragen.',
      successWithRefund: 'Ihr Termin wurde storniert. Eine Rückerstattung von {amount} wird innerhalb von 5–10 Werktagen bearbeitet.',
      successContactStudio: 'Ihr Termin wurde storniert. Bitte kontaktieren Sie das Studio direkt für Ihre Rückerstattung.',
      verifyEmail: 'Ihre E-Mail-Adresse',
      verifyEmailPlaceholder: 'Geben Sie die bei der Buchung verwendete E-Mail ein',
      lateBlockedTitle: 'Online-Stornierung nicht mehr möglich',
      lateBlockedText: 'Innerhalb von {hours} Stunden vor Ihrem Termin ist eine Online-Stornierung nicht möglich. Bitte rufen Sie das Studio an, um abzusagen.',
      lateBlockedNoPhone: 'Innerhalb von {hours} Stunden vor Ihrem Termin ist eine Online-Stornierung nicht möglich. Bitte kontaktieren Sie das Studio direkt, um abzusagen.',
      hoursRemaining: 'Noch {hours} Std.',
      callStudio: 'Studio anrufen'
    },
    announcement: {
      close: 'Schließen',
      previous: 'Vorherige',
      next: 'Nächste',
      noshow: "Heute nicht mehr anzeigen"
    },
    offline: {
      title: 'Vorübergehend geschlossen',
      body: 'Die Online-Buchung ist derzeit nicht verfügbar. Bitte schauen Sie später noch einmal vorbei.',
      bookingRefused: 'Dieses Studio nimmt vorübergehend keine Online-Buchungen an. Bitte laden Sie die Seite neu.'
    },
    loadLocation: {
      title: 'Standort',
      subtitle: 'Finden Sie uns auf der Karte',
      loadMap: 'Klicken Sie hier, um Google Maps zu laden',
      mapPrivacyNotice: 'Beim Laden der Karte werden Daten an Google übermittelt'
    },
    rating: {
      title: 'Wie lief die Buchung?',
      hint: 'Optional. Hilft uns, das Buchen einfacher zu machen.',
      starLabel: '{n} von 5 Sternen',
      improveTitle: 'Was könnte besser sein?',
      reasonAppointment: 'Einen Termin finden',
      reasonService: 'Einen Service auswählen',
      reasonTherapist: 'Eine Therapeutin auswählen',
      reasonConfusing: 'Die Seite war verwirrend',
      reasonSlow: 'Es hat zu lange gedauert',
      reasonOther: 'Etwas anderes',
      commentPlaceholder: 'Erzählen Sie uns kurz mehr (optional)',
      submit: 'Senden',
      skip: 'Überspringen',
      thanks: 'Danke, das hilft uns wirklich.'
    }
  },
  it: {
    nav: {
      home: 'Home',
      offers: 'Offerte',
      services: 'Prenotazione',
      atmosphere: 'Atmosfera',
      location: 'Posizione',
      massages: 'Massaggi',
      visitUs: 'Vieni a trovarci',
      faq: 'FAQ'
    },
    services: {
      title: 'Clicca qui per vedere le disponibilità!',
      duration: 'Durata',
      startingFrom: 'A partire da',
      minutes: 'Minuti',
      bookNow: 'Prenota ora',
      loading: 'Caricamento servizi...',
      popular: 'Popolare',
      pairBadge: 'In coppia',
      more: 'Altro',
      less: 'Meno'
    },
    atmosphere: {
      title: 'Atmosfera',
      subtitle: 'Scopri il nostro ambiente rilassante'
    },
    location: {
      title: 'Posizione',
      subtitle: 'Trovaci sulla mappa'
    },
    modal: {
      title: 'Scegli il Tipo di Massaggio',
      type1: 'Massaggio Tradizionale Tailandese',
      type2: 'Massaggio Tailandese con Olio',
      type3: 'Massaggio Aromatico Tailandese',
      close: 'Chiudi',
      dateTimeTitle: 'Seleziona Data e Ora',
      selectDate: 'Seleziona Data',
      selectTime: 'Seleziona Ora',
      firstName: 'Nome',
      lastName: 'Cognome',
      phone: 'Numero di Telefono',
      email: 'Indirizzo Email',
      emailFollowUpNotice: '{studio} potrebbe inviarLe un\'e-mail dopo la Sua visita per chiederLe un feedback e proporLe nuove prenotazioni. Può annullare l\'iscrizione in qualsiasi momento.',
      persons: 'Numero di Persone',
      back: 'Indietro',
      confirm: 'Prenota ora',
      termsAcceptance: 'Ho letto e accetto i Termini e Condizioni e il Diritto di Recesso.',
      totalPrice: 'Prezzo totale',
      sum: 'Somma',
      total: 'Totale',
      specialWish: 'Desideri speciali / Note',
      specialWishPlaceholder: 'Richieste speciali o note per la sua visita... (facoltativo)',
      therapist: 'Terapista',
      therapist1: 'Terapista 1',
      therapist2: 'Terapista 2',
      therapistNoPreference: 'Nessuna preferenza',
      therapistUnavailable: 'Questo terapista è già prenotato all\'orario selezionato. Scegli un orario o un terapista diverso.',
      sameDayLeadTime: "Le prenotazioni per oggi richiedono almeno {minutes} minuti di preavviso. Scegli un orario successivo.",
      therapistTimeFiltered: "L'orario precedentemente selezionato non è disponibile con questo terapista. Si prega di scegliere un orario disponibile dalla griglia sopra.",
      maxPersonsHint: 'Per prenotare per più di 2 persone, effettuare una prenotazione separata.',
      paymentTitle: 'Scegli il metodo di pagamento',
      paymentMethod: 'Come preferisci pagare?',
      payFull: 'Paga per intero ({amount})',
      payDeposit: 'Paga acconto ora ({amount}), il resto in loco',
      payCash: 'Paga in contanti in loco',
      cardDetails: 'Dati della carta',
      confirmAndPay: 'Conferma e paga {amount}',
      paymentProcessing: 'Elaborazione pagamento...',
      paymentError: 'Pagamento fallito. Si prega di riprovare.',
      securePayment: 'Pagamento sicuro powered by Stripe'
    },
    notification: {
      close: 'Chiudi',
      bookingConfirmed: 'Prenotazione Confermata!',
      confirmationEmail: 'La conferma di prenotazione sarà inviata alla tua email',
      selectDateFirst: 'Seleziona prima una data per vedere i tempi disponibili',
      loadingTimeSlots: 'Caricamento orari disponibili...',
      noAvailableTimes: 'Nessun orario disponibile per questa data. Seleziona un\'altra data.',
      bookingFailed: 'Prenotazione Fallita',
      checkInformation: 'Controlla le tue informazioni e riprova',
      networkError: 'Errore di Rete',
      checkConnection: 'Controlla la tua connessione e riprova',
      date: 'Data',
      time: 'Ora',
      massageType: 'Tipo di Massaggio',
      duration: 'Durata',
      amountPaid: 'Importo Pagato',
      remainingAtVenue: 'Resto in loco',
      refundPolicy: 'Politica di Rimborso',
      cancellationPolicy: 'Politica di cancellazione',
      noRefunds: 'Nessun rimborso',
      refundWindow: 'Cancellazione accettata fino a {hours} ore prima del tuo appuntamento.',
      cancellationFee: 'Verrà applicata una commissione di cancellazione di €5.',
      lateCancellationPolicy: 'Se dovesse capitarti un imprevisto e non potessi venire al tuo appuntamento, ti chiediamo di avvisarci almeno {hours} ore prima, esclusivamente per telefono.',
      timeRemaining: 'Inizia tra'
    },
    hero: {
      voucherBtn: 'Acquista Voucher',
      openUntil: 'Oggi aperto fino alle {time}',
      opensAt: 'Oggi apre alle {time}',
      closedToday: 'Oggi chiuso'
    },
    cta: {
      fromPrice: 'da {price} · {minutes} min'
    },
    trust: {
      sameDay: 'Appuntamenti disponibili in giornata',
      cancelFree: 'Cancellazione gratuita fino a {hours} h prima',
      payOnline: 'Pagamento online sicuro',
      payAtVenue: 'Paga al tuo appuntamento'
    },
    footer: {
      impressum: 'Impressum',
      dataProtection: 'Protezione dei Dati',
      terms: 'Termini e Condizioni',
      address: 'Indirizzo',
      openingHours: 'Orari di Apertura',
      email: 'Email',
      rights: 'Tutti i diritti riservati.',
      poweredBy: 'powered by'
    },
    common: {
      closed: 'Chiuso',
      language: 'Lingua',
      expand: 'Espandi',
      collapse: 'Comprimi',
      previousImage: 'Immagine precedente',
      nextImage: 'Immagine successiva',
      imageViewer: 'Visualizzatore di immagini',
      loading: 'Caricamento…',
      min: 'min'
    },
    validation: {
      phoneInvalid: 'Il numero di telefono deve iniziare con + o con una cifra.',
      emailInvalid: 'Inserisci un indirizzo email valido.',
      notesInvalid: 'I caratteri @ \' $ # non sono consentiti.',
      required: 'Questo campo è obbligatorio.'
    },
    booking: {
      processing: 'Elaborazione in corso…',
      calculating: 'Calcolo in corso…',
      datePlaceholder: 'Seleziona una data',
      loadError: 'Impossibile caricare i dati più recenti. Riprova.',
      slotsLoadError: 'Impossibile caricare gli orari disponibili. Riprova.',
      typeUnavailableOnDay: '{type} non è disponibile in questo giorno. Scegli un altro giorno o un altro massaggio.',
      timeNoLongerAvailable: 'Questo orario non è più disponibile. Scegli un altro orario.',
      chooseType: 'Scegli il tuo massaggio',
      chooseTime: 'Scegli un orario',
      yourDetails: 'I tuoi dati',
      confirmAndPay: 'Conferma e paga',
      confirmBooking: 'Conferma prenotazione',
      stepOf: 'Passaggio {current} di {total}',
      change: 'Modifica',
      continue: 'Continua',
      moreDates: 'Altre date',
      fewerDates: 'Meno date',
      morning: 'Mattina',
      afternoon: 'Pomeriggio',
      evening: 'Sera',
      slotsLeft: '{n} disponibili',
      anyTherapist: 'Qualsiasi terapista',
      confirmAmount: 'Conferma · {amount}',
      payAmount: 'Paga {amount}',
      retry: 'Riprova',
      done: 'Fatto',
      duration: 'Durata',
      selectDuration: 'Scegli la durata',
      durationTimeFiltered: 'L\'orario scelto non è disponibile per questa durata. Scegli un altro orario.',
      pairMassageHint: 'Massaggio di coppia – prenotazione per 2 persone',
      priceForTwo: 'per 2 persone',
      priceBase: 'Prezzo base',
      priceSurcharge: 'Supplemento',
      priceDiscount: 'Sconto',
      priceDetails: 'Dettagli',
      priceBreakdown: 'Dettaglio prezzo',
      prefilledHint: 'Abbiamo inserito i suoi dati dalla sua ultima prenotazione.',
      prefilledClear: 'Non è lei? Cancella'
    },
    visit: {
      loadingMap: 'Caricamento della mappa…',
      title: 'Vieni a trovarci',
      openInMaps: 'Apri in Google Maps',
      call: 'Chiama',
      today: 'Oggi'
    },
    days: {
      monday: 'Lunedì',
      tuesday: 'Martedì',
      wednesday: 'Mercoledì',
      thursday: 'Giovedì',
      friday: 'Venerdì',
      saturday: 'Sabato',
      sunday: 'Domenica'
    },
    offers: {
      title: 'I Nostri Massaggi',
      loading: 'Caricamento offerte...',
      popular: 'Popolare',
      bookFor: 'Prenota per:',
      selectDuration: 'Seleziona la durata',
      priceAdjustment: 'Adeguamento prezzo',
      noDescription: 'Nessuna descrizione disponibile.',
      traditional: {
        title: 'Massaggio Thai Tradizionale',
        description: 'Un massaggio profondo basato sulla pressione che allunga e rilassa i muscoli usando mani, gomiti e piedi. Spesso eseguito su un tappetino completamente vestiti.'
      },
      oil: {
        title: 'Massaggio Thai con Olio',
        description: 'Combina tecniche thailandesi delicate con movimenti fluidi di olio per rilassare i muscoli, migliorare la circolazione e nutrire la pelle.'
      },
      aroma: {
        title: 'Massaggio Thai Aromatico',
        description: 'Utilizza oli essenziali aromatici insieme a tecniche di massaggio thailandese per rilassare corpo e mente migliorando l\'umore e il benessere.'
      }
    },
    faq: {
      title: 'Massaggio Thai – Domande Frequenti',
      showAll: 'Mostra tutte le {count} domande',
      showLess: 'Mostra meno',
      cat1: "Cos'è il Massaggio Thai?",
      cat2: 'Perché il Massaggio Thai è Diverso',
      cat3: 'Cosa Aspettarsi',
      cat4: 'Perché i Clienti Scelgono il Massaggio Thai',
      cat5: 'Prima della Visita',
      cat6: 'Prenotazione & Esperienza',
      cat7: 'Risultati & Benefici',
      q1: "Cos'è il massaggio thai?",
      a1: "Il massaggio thai è una terapia tradizionale per tutto il corpo che combina stretching, agopressione e movimenti ritmici. Aiuta a rilasciare le tensioni, migliorare la flessibilità e ripristinare l'equilibrio energetico — rendendolo sia rilassante che rivitalizzante.",
      q2: "Come si differenzia il massaggio thai da un massaggio all'olio o da spa?",
      a2: "A differenza dei massaggi all'olio o da spa che si concentrano principalmente sul rilassamento muscolare, il massaggio thai è più attivo e basato sul movimento. Viene eseguito completamente vestiti su un comodo tappetino e include stretching assistito, simile allo yoga.",
      q3: 'Come si differenzia il massaggio thai da quello ai tessuti profondi?',
      a3: 'Il massaggio ai tessuti profondi si concentra sulla forte pressione su muscoli specifici tesi. Il massaggio thai lavora su tutto il corpo attraverso stretching, punti di pressione e movimento, offrendo un reset completo invece di trattare solo aree di dolore isolate.',
      q4: 'Come si differenzia il massaggio thai da quello sportivo?',
      a4: 'Il massaggio sportivo è progettato per atleti e recupero da infortuni. Il massaggio thai è adatto a tutti e si concentra sulla riduzione della rigidità, il miglioramento della mobilità e il sollievo dalle tensioni quotidiane.',
      q5: 'Come ci si sente durante un massaggio thai?',
      a5: 'Si sperimenterà una combinazione di stretching delicato, pressione ferma e movimenti fluidi. Molti ospiti lo descrivono come profondamente rilassante ma allo stesso tempo energizzante.',
      q6: 'Il massaggio thai fa male?',
      a6: 'No — non dovrebbe mai fare male. La pressione e lo stretching vengono sempre adattati al vostro livello di comfort. Potete richiedere una intensità più leggera o più forte in qualsiasi momento.',
      q7: 'Mi sentirò meglio dopo una sola sessione?',
      a7: 'La maggior parte degli ospiti sente un immediato sollievo dalla tensione, una migliore flessibilità e un corpo più leggero e rilassato dopo solo una sessione.',
      q8: 'Perché dovrei scegliere il massaggio thai invece di un massaggio rilassante normale?',
      a8: "Il massaggio thai va oltre il rilassamento. Aiuta a rilasciare la rigidità profonda, migliora la mobilità e ripristina il flusso di energia in tutto il corpo. È particolarmente benefico se si è spesso seduti, si hanno muscoli tesi o si desidera un reset completo del corpo.",
      q9: "Devo preparare qualcosa prima dell'appuntamento?",
      a9: 'Non è necessaria alcuna preparazione. Basta arrivare qualche minuto prima e indossare abiti comodi.',
      q10: 'Ho bisogno di esperienza precedente?',
      a10: 'Assolutamente no. Il massaggio thai è adatto ai principianti e ai visitatori per la prima volta.',
      q11: 'Come posso prenotare un appuntamento?',
      a11: 'Puoi prenotare online in pochi secondi o chiamare direttamente il negozio. I walk-in sono benvenuti in base alla disponibilità.',
      q12: 'Posso scegliere il livello di pressione?',
      a12: 'Sì. Puoi scegliere pressione leggera, media o forte in base alle tue preferenze.',
      q13: 'Posso venire con un amico o un partner?',
      a13: 'Sì, molte sedi offrono trattamenti in coppia o fianco a fianco a seconda della disponibilità delle sale.',
      q14: 'Quali sono i principali benefici del massaggio thai?',
      a14: 'Il massaggio thai può aiutare a ridurre lo stress, migliorare la flessibilità, aumentare la circolazione e alleviare la rigidità muscolare.',
      q15: 'Con quale frequenza dovrei fare un massaggio thai?',
      a15: 'Molti clienti prenotano una volta alla settimana o ogni due settimane per mantenere la flessibilità e ridurre lo stress.',
      ctaTitle: 'Pronto a sentirti meglio?',
      ctaBody: 'Prenota ora il tuo massaggio thai e goditi un reset completo del corpo che ti lascia rilassato, flessibile e ricaricato.',
      ctaBullet1: 'Appuntamenti in giornata spesso disponibili',
      ctaBullet2: 'Walk-in benvenuti (in base alla disponibilità)',
    },
    cancellation: {
      title: 'Cancella Appuntamento',
      loading: 'Caricamento dettagli prenotazione...',
      error: 'Impossibile caricare la prenotazione',
      close: 'Chiudi',
      bookingDetails: 'Dettagli Prenotazione',
      bookingId: 'ID Prenotazione',
      customer: 'Cliente',
      service: 'Servizio',
      massageType: 'Tipo di Massaggio',
      date: 'Data',
      time: 'Ora',
      persons: 'Persone',
      totalPrice: 'Prezzo Totale',
      warningTitle: 'Cancellazione Last Minute',
      warningMessage: 'Il tuo appuntamento è tra meno di {hours} ore. Le cancellazioni last minute potrebbero essere soggette a tariffe.',
      hours: 'ore',
      confirmTitle: 'Conferma Cancellazione',
      reasonLabel: 'Motivo della cancellazione (opzionale)',
      reasonPlaceholder: 'Per favore facci sapere perché devi cancellare...',
      processing: 'Elaborazione...',
      confirmCancel: 'Sì, Cancella Appuntamento',
      keepBooking: 'Mantieni la Prenotazione',
      successTitle: 'Appuntamento Cancellato',
      successMessage: 'Il tuo appuntamento è stato cancellato con successo.',
      invalidLink: 'Link di cancellazione non valido',
      cannotCancel: 'Questa prenotazione non può essere cancellata',
      confirmPrompt: 'Sei sicuro di voler cancellare questo appuntamento? Questa azione non può essere annullata.',
      errorMessage: 'Impossibile cancellare l\'appuntamento. Riprova o contattaci direttamente.',
      refundNoticeTitle: 'Verrà emesso un rimborso',
      refundNoticeBody: 'Un rimborso di {amount} verrà elaborato (commissione di cancellazione di €5 applicata).',
      outsideWindowTitle: 'Finestra di cancellazione scaduta',
      outsideWindowBody: 'Contatta direttamente lo studio per richiedere il tuo rimborso.',
      contactStudioInstruction: 'Per richiedere un rimborso, contatta direttamente lo studio.',
      successWithRefund: 'Il tuo appuntamento è stato cancellato. Un rimborso di {amount} verrà elaborato entro 5–10 giorni lavorativi.',
      successContactStudio: 'Il tuo appuntamento è stato cancellato. Contatta direttamente lo studio per organizzare il tuo rimborso.',
      verifyEmail: 'Il tuo indirizzo email',
      verifyEmailPlaceholder: 'Inserisci l\'email utilizzata per la prenotazione',
      lateBlockedTitle: 'Cancellazione online non più possibile',
      lateBlockedText: 'Nelle {hours} ore precedenti l\'appuntamento non è possibile cancellare online. Chiama lo studio per cancellare.',
      lateBlockedNoPhone: 'Nelle {hours} ore precedenti l\'appuntamento non è possibile cancellare online. Contatta direttamente lo studio per cancellare.',
      hoursRemaining: 'Mancano {hours} h',
      callStudio: 'Chiama lo studio'
    },
    announcement: {
      close: 'Chiudi',
      previous: 'Precedente',
      next: 'Successivo',
      noshow: "Non mostrare oggi"
    },
    offline: {
      title: 'Temporaneamente chiuso',
      body: 'La prenotazione online non è al momento disponibile. Ti invitiamo a riprovare più tardi.',
      bookingRefused: 'Questo studio al momento non accetta prenotazioni online. Ricarica la pagina.'
    },
    loadLocation: {
      title: 'Posizione',
      subtitle: 'Trovaci sulla mappa',
      loadMap: 'Clicca per caricare Google Maps',
      mapPrivacyNotice: 'Il caricamento della mappa trasmetterà dati a Google'
    },
    rating: {
      title: "Com'è andata la prenotazione?",
      hint: 'Facoltativo. Ci aiuta a rendere la prenotazione più semplice.',
      starLabel: '{n} stelle su 5',
      improveTitle: 'Cosa potrebbe andare meglio?',
      reasonAppointment: 'Trovare un orario',
      reasonService: 'Scegliere un servizio',
      reasonTherapist: 'Scegliere un/una terapista',
      reasonConfusing: 'La pagina non era chiara',
      reasonSlow: 'Ci è voluto troppo tempo',
      reasonOther: 'Altro',
      commentPlaceholder: 'Raccontaci qualcosa in più (facoltativo)',
      submit: 'Invia',
      skip: 'Salta',
      thanks: 'Grazie, ci aiuta davvero.'
    }
  },
  fr: {
    nav: {
      home: 'Accueil',
      offers: 'Offres',
      services: 'Réservation',
      atmosphere: 'Ambiance',
      location: 'Emplacement',
      massages: 'Massages',
      visitUs: 'Nous rendre visite',
      faq: 'FAQ'
    },
    services: {
      title: 'Cliquez ici pour voir les disponibilités !',
      duration: 'Durée',
      startingFrom: 'À partir de',
      minutes: 'Minutes',
      bookNow: 'Réserver maintenant',
      loading: 'Chargement des services...',
      popular: 'Populaire',
      pairBadge: 'À deux',
      more: 'Plus',
      less: 'Moins'
    },
    atmosphere: {
      title: 'Ambiance',
      subtitle: 'Découvrez notre environnement relaxant'
    },
    location: {
      title: 'Emplacement',
      subtitle: 'Trouvez-nous sur la carte'
    },
    modal: {
      title: 'Choisissez Votre Type de Massage',
      type1: 'Massage Thaïlandais Traditionnel',
      type2: 'Massage Thaïlandais à l\'Huile',
      type3: 'Massage Thaïlandais Aromatique',
      close: 'Fermer',
      dateTimeTitle: 'Sélectionnez Date et Heure',
      selectDate: 'Sélectionner la Date',
      selectTime: 'Sélectionner l\'Heure',
      firstName: 'Prénom',
      lastName: 'Nom de Famille',
      phone: 'Numéro de Téléphone',
      email: 'Adresse Email',
      emailFollowUpNotice: '{studio} peut vous envoyer un e-mail après votre visite pour vous demander votre avis et vous proposer de nouvelles réservations. Vous pouvez vous désabonner à tout moment.',
      persons: 'Nombre de Personnes',
      back: 'Retour',
      confirm: 'Réserver maintenant',
      termsAcceptance: 'J\'ai lu et j\'accepte les Conditions Générales et le Droit de Rétractation.',
      totalPrice: 'Prix total',
      sum: 'Somme',
      total: 'Total',
      specialWish: 'Souhaits spéciaux / Notes',
      specialWishPlaceholder: 'Demandes spéciales ou notes pour votre visite... (optionnel)',
      therapist: 'Thérapeute',
      therapist1: 'Thérapeute 1',
      therapist2: 'Thérapeute 2',
      therapistNoPreference: 'Pas de préférence',
      therapistUnavailable: 'Ce thérapeute est déjà réservé à l\'heure sélectionnée. Veuillez choisir un autre horaire ou thérapeute.',
      sameDayLeadTime: "Les réservations pour aujourd'hui doivent être faites au moins {minutes} minutes à l'avance. Veuillez choisir un horaire plus tard.",
      therapistTimeFiltered: "Le créneau horaire précédemment sélectionné n'est pas disponible avec ce thérapeute. Veuillez choisir un créneau disponible dans le tableau ci-dessus.",
      maxPersonsHint: 'Pour réserver pour plus de 2 personnes, veuillez effectuer une réservation séparée.',
      paymentTitle: 'Choisir le mode de paiement',
      paymentMethod: 'Comment souhaitez-vous payer ?',
      payFull: 'Payer en totalité ({amount})',
      payDeposit: 'Payer un acompte maintenant ({amount}), le reste sur place',
      payCash: 'Payer en espèces sur place',
      cardDetails: 'Détails de la carte',
      confirmAndPay: 'Confirmer & payer {amount}',
      paymentProcessing: 'Traitement du paiement...',
      paymentError: 'Paiement échoué. Veuillez réessayer.',
      securePayment: 'Paiement sécurisé powered by Stripe'
    },
    notification: {
      close: 'Fermer',
      bookingConfirmed: 'Réservation Confirmée!',
      confirmationEmail: 'La confirmation de réservation sera envoyée à votre email',
      selectDateFirst: 'Veuillez d\'abord sélectionner une date pour voir les heures disponibles',
      loadingTimeSlots: 'Chargement des heures disponibles...',
      noAvailableTimes: 'Aucune heure disponible pour cette date. Veuillez sélectionner une autre date.',
      bookingFailed: 'Réservation Échouée',
      checkInformation: 'Veuillez vérifier vos informations et réessayer',
      networkError: 'Erreur Réseau',
      checkConnection: 'Veuillez vérifier votre connexion et réessayer',
      date: 'Date',
      time: 'Heure',
      massageType: 'Type de Massage',
      duration: 'Durée',
      amountPaid: 'Montant Payé',
      remainingAtVenue: 'Reste à payer sur place',
      refundPolicy: 'Politique de Remboursement',
      cancellationPolicy: 'Conditions d\'annulation',
      noRefunds: 'Aucun remboursement',
      refundWindow: 'Annulation acceptée jusqu\'à {hours} heures avant votre rendez-vous.',
      cancellationFee: 'Des frais d\'annulation de €5 s\'appliquent.',
      lateCancellationPolicy: 'Si un imprévu survient et que vous ne pouvez pas honorer votre rendez-vous, nous vous prions de nous prévenir au moins {hours} heures à l\'avance, uniquement par téléphone.',
      timeRemaining: 'Début dans'
    },
    hero: {
      voucherBtn: 'Acheter un Bon Cadeau',
      openUntil: 'Ouvert aujourd\'hui jusqu\'à {time}',
      opensAt: 'Ouvre aujourd\'hui à {time}',
      closedToday: 'Fermé aujourd\'hui'
    },
    cta: {
      fromPrice: 'à partir de {price} · {minutes} min'
    },
    trust: {
      sameDay: 'Rendez-vous disponibles le jour même',
      cancelFree: 'Annulation gratuite jusqu’à {hours} h avant',
      payOnline: 'Paiement en ligne sécurisé',
      payAtVenue: 'Paiement lors du rendez-vous'
    },
    footer: {
      impressum: 'Mentions Légales',
      dataProtection: 'Protection des Données',
      terms: 'Conditions Générales',
      address: 'Adresse',
      openingHours: 'Horaires d\'Ouverture',
      email: 'Email',
      rights: 'Tous droits réservés.',
      poweredBy: 'propulsé par'
    },
    common: {
      closed: 'Fermé',
      language: 'Langue',
      expand: 'Développer',
      collapse: 'Réduire',
      previousImage: 'Image précédente',
      nextImage: 'Image suivante',
      imageViewer: 'Visionneuse d\'images',
      loading: 'Chargement…',
      min: 'min'
    },
    validation: {
      phoneInvalid: 'Le numéro de téléphone doit commencer par + ou par un chiffre.',
      emailInvalid: 'Veuillez saisir une adresse e-mail valide.',
      notesInvalid: 'Les caractères @ \' $ # ne sont pas autorisés.',
      required: 'Ce champ est obligatoire.'
    },
    booking: {
      processing: 'Traitement en cours…',
      calculating: 'Calcul en cours…',
      datePlaceholder: 'Choisir une date',
      loadError: 'Impossible de charger les dernières données. Veuillez réessayer.',
      slotsLoadError: 'Impossible de charger les horaires disponibles. Veuillez réessayer.',
      typeUnavailableOnDay: '{type} n\'est pas disponible ce jour-là. Veuillez choisir un autre jour ou un autre massage.',
      timeNoLongerAvailable: 'Cet horaire n\'est plus disponible. Veuillez choisir un autre horaire.',
      chooseType: 'Choisissez votre massage',
      chooseTime: 'Choisissez un horaire',
      yourDetails: 'Vos coordonnées',
      confirmAndPay: 'Confirmer et payer',
      confirmBooking: 'Confirmer la réservation',
      stepOf: 'Étape {current} sur {total}',
      change: 'Modifier',
      continue: 'Continuer',
      moreDates: 'Plus de dates',
      fewerDates: 'Moins de dates',
      morning: 'Matin',
      afternoon: 'Après-midi',
      evening: 'Soir',
      slotsLeft: '{n} restantes',
      anyTherapist: 'Peu importe',
      confirmAmount: 'Confirmer · {amount}',
      payAmount: 'Payer {amount}',
      retry: 'Réessayer',
      done: 'Terminé',
      duration: 'Durée',
      selectDuration: 'Choisir la durée',
      durationTimeFiltered: 'L\'heure choisie n\'est pas disponible pour cette durée. Veuillez choisir une autre heure.',
      pairMassageHint: 'Massage en duo – réservation pour 2 personnes',
      priceForTwo: 'pour 2 personnes',
      priceBase: 'Prix de base',
      priceSurcharge: 'Supplément',
      priceDiscount: 'Remise',
      priceDetails: 'Détails',
      priceBreakdown: 'Détail du prix',
      prefilledHint: 'Nous avons repris vos coordonnées de votre dernière réservation.',
      prefilledClear: 'Ce n\'est pas vous ? Effacer'
    },
    visit: {
      loadingMap: 'Chargement de la carte…',
      title: 'Nous rendre visite',
      openInMaps: 'Ouvrir dans Google Maps',
      call: 'Appeler',
      today: 'Aujourd’hui'
    },
    days: {
      monday: 'Lundi',
      tuesday: 'Mardi',
      wednesday: 'Mercredi',
      thursday: 'Jeudi',
      friday: 'Vendredi',
      saturday: 'Samedi',
      sunday: 'Dimanche'
    },
    offers: {
      title: 'Nos Massages',
      loading: 'Chargement des offres...',
      popular: 'Populaire',
      bookFor: 'Réserver pour :',
      selectDuration: 'Choisir la durée',
      priceAdjustment: 'Ajustement de prix',
      noDescription: 'Aucune description disponible.',
      traditional: {
        title: 'Massage Thaï Traditionnel',
        description: 'Un massage profond basé sur la pression qui étire et détend les muscles en utilisant les mains, les coudes et les pieds. Souvent effectué sur un tapis entièrement habillé.'
      },
      oil: {
        title: 'Massage Thaï à l\'Huile',
        description: 'Combine des techniques thaïlandaises douces avec des mouvements fluides d\'huile pour détendre les muscles, améliorer la circulation et nourrir la peau.'
      },
      aroma: {
        title: 'Massage Thaï Aromatique',
        description: 'Utilise des huiles essentielles aromatiques avec des techniques de massage thaïlandais pour détendre le corps et l\'esprit tout en améliorant l\'humeur et le bien-être.'
      }
    },
    faq: {
      title: 'Massage Thaïlandais – Questions Fréquentes',
      showAll: 'Afficher les {count} questions',
      showLess: 'Afficher moins',
      cat1: "Qu'est-ce que le Massage Thaïlandais ?",
      cat2: 'Pourquoi le Massage Thaïlandais est Différent',
      cat3: "À Quoi s'Attendre",
      cat4: 'Pourquoi les Clients Choisissent le Massage Thaïlandais',
      cat5: 'Avant Votre Visite',
      cat6: 'Réservation & Expérience',
      cat7: 'Résultats & Bienfaits',
      q1: "Qu'est-ce que le massage thaïlandais ?",
      a1: "Le massage thaïlandais est une thérapie corporelle traditionnelle qui combine des étirements, l'acupression et des mouvements rythmiques. Il aide à libérer les tensions, améliorer la flexibilité et restaurer l'équilibre énergétique — le rendant à la fois relaxant et revitalisant.",
      q2: "En quoi le massage thaïlandais est-il différent d'un massage à l'huile ou spa ?",
      a2: "Contrairement aux massages à l'huile ou spa qui se concentrent sur la relaxation musculaire, le massage thaïlandais est plus actif et basé sur le mouvement. Il est pratiqué entièrement habillé sur un tapis confortable et comprend des étirements assistés, similaires au yoga.",
      q3: 'En quoi le massage thaïlandais est-il différent du massage des tissus profonds ?',
      a3: "Le massage des tissus profonds se concentre sur une forte pression sur des muscles spécifiques. Le massage thaïlandais travaille sur tout le corps par des étirements, des points de pression et des mouvements, offrant une remise à zéro complète.",
      q4: 'En quoi le massage thaïlandais est-il différent du massage sportif ?',
      a4: 'Le massage sportif est conçu pour les athlètes et la récupération après blessure. Le massage thaïlandais convient à tous et se concentre sur la réduction de la raideur, la mobilité et le soulagement des tensions quotidiennes.',
      q5: "Qu'est-ce que l'on ressent pendant un massage thaïlandais ?",
      a5: "Vous vivrez une combinaison d'étirements doux, de pression ferme et de mouvements fluides. De nombreux clients le décrivent comme profondément relaxant et énergisant à la fois.",
      q6: 'Le massage thaïlandais est-il douloureux ?',
      a6: "Non — il ne devrait jamais être douloureux. La pression et les étirements sont toujours adaptés à votre niveau de confort. Vous pouvez demander une intensité différente à tout moment.",
      q7: 'Me sentirai-je mieux après une seule séance ?',
      a7: 'La plupart des clients ressentent un soulagement immédiat des tensions, une meilleure flexibilité et un corps plus léger et détendu après seulement une séance.',
      q8: "Pourquoi choisir le massage thaïlandais plutôt qu'un massage de relaxation normal ?",
      a8: "Le massage thaïlandais va au-delà de la relaxation. Il aide à libérer la raideur profonde, améliore la mobilité et restaure le flux d'énergie dans tout le corps.",
      q9: 'Dois-je préparer quelque chose avant mon rendez-vous ?',
      a9: "Aucune préparation n'est nécessaire. Arrivez simplement quelques minutes à l'avance et portez des vêtements confortables.",
      q10: "Ai-je besoin d'une expérience préalable ?",
      a10: 'Pas du tout. Le massage thaïlandais convient aux débutants et aux premiers visiteurs.',
      q11: 'Comment puis-je réserver un rendez-vous ?',
      a11: 'Vous pouvez réserver en ligne en quelques secondes ou appeler directement le salon. Les visites sans rendez-vous sont les bienvenues selon les disponibilités.',
      q12: 'Puis-je choisir le niveau de pression ?',
      a12: 'Oui. Vous pouvez choisir une pression légère, moyenne ou forte selon vos préférences.',
      q13: 'Puis-je venir avec un ami ou un partenaire ?',
      a13: 'Oui, de nombreux établissements proposent des soins en duo selon la disponibilité des salles.',
      q14: 'Quels sont les principaux avantages du massage thaïlandais ?',
      a14: "Le massage thaïlandais peut aider à réduire le stress, améliorer la flexibilité, augmenter la circulation et soulager la raideur musculaire.",
      q15: 'À quelle fréquence devrais-je faire un massage thaïlandais ?',
      a15: "De nombreux clients réservent une fois par semaine ou toutes les deux semaines pour maintenir la flexibilité et prévenir l'accumulation de tensions.",
      ctaTitle: 'Prêt à vous sentir mieux ?',
      ctaBody: "Réservez votre massage thaïlandais maintenant et profitez d'une remise à zéro complète qui vous laisse détendu, flexible et rechargé.",
      ctaBullet1: 'Rendez-vous le jour même souvent disponibles',
      ctaBullet2: 'Visites sans rendez-vous bienvenues (selon disponibilité)',
    },
    cancellation: {
      title: 'Annuler le Rendez-vous',
      loading: 'Chargement des détails de réservation...',
      error: 'Impossible de charger la réservation',
      close: 'Fermer',
      bookingDetails: 'Détails de la Réservation',
      bookingId: 'ID de Réservation',
      customer: 'Client',
      service: 'Service',
      massageType: 'Type de Massage',
      date: 'Date',
      time: 'Heure',
      persons: 'Personnes',
      totalPrice: 'Prix Total',
      warningTitle: 'Annulation de Dernière Minute',
      warningMessage: 'Votre rendez-vous est dans moins de {hours} heures. Les annulations de dernière minute peuvent être sujettes à des frais.',
      hours: 'heures',
      confirmTitle: 'Confirmer l\'Annulation',
      reasonLabel: 'Raison de l\'annulation (optionnel)',
      reasonPlaceholder: 'Veuillez nous faire savoir pourquoi vous devez annuler...',
      processing: 'Traitement...',
      confirmCancel: 'Oui, Annuler le Rendez-vous',
      keepBooking: 'Conserver ma Réservation',
      successTitle: 'Rendez-vous Annulé',
      successMessage: 'Votre rendez-vous a été annulé avec succès.',
      invalidLink: 'Lien d\'annulation invalide',
      cannotCancel: 'Cette réservation ne peut pas être annulée',
      confirmPrompt: 'Êtes-vous sûr de vouloir annuler ce rendez-vous ? Cette action ne peut pas être annulée.',
      errorMessage: 'Échec de l\'annulation du rendez-vous. Veuillez réessayer ou nous contacter directement.',
      refundNoticeTitle: 'Un remboursement sera émis',
      refundNoticeBody: 'Un remboursement de {amount} sera traité (frais d\'annulation de €5 appliqués).',
      outsideWindowTitle: 'Délai d\'annulation dépassé',
      outsideWindowBody: 'Veuillez contacter le studio directement pour demander votre remboursement.',
      contactStudioInstruction: 'Pour demander un remboursement, veuillez contacter le studio directement.',
      successWithRefund: 'Votre rendez-vous a été annulé. Un remboursement de {amount} sera traité sous 5 à 10 jours ouvrables.',
      successContactStudio: 'Votre rendez-vous a été annulé. Veuillez contacter le studio directement pour organiser votre remboursement.',
      verifyEmail: 'Votre adresse email',
      verifyEmailPlaceholder: 'Saisissez l\'email utilisé lors de la réservation',
      lateBlockedTitle: 'Annulation en ligne plus possible',
      lateBlockedText: 'L\'annulation en ligne n\'est pas possible dans les {hours} heures précédant votre rendez-vous. Veuillez appeler le studio pour annuler.',
      lateBlockedNoPhone: 'L\'annulation en ligne n\'est pas possible dans les {hours} heures précédant votre rendez-vous. Veuillez contacter directement le studio pour annuler.',
      hoursRemaining: 'Encore {hours} h',
      callStudio: 'Appeler le studio'
    },
    announcement: {
      close: 'Fermer',
      previous: 'Précédent',
      next: 'Suivant',
      noshow: "Ne pas montrer aujourd'hui"
    },
    offline: {
      title: 'Temporairement fermé',
      body: 'La réservation en ligne est momentanément indisponible. Merci de réessayer plus tard.',
      bookingRefused: "Ce studio n'accepte temporairement pas de réservations en ligne. Veuillez recharger la page."
    },
    loadLocation: {
      title: 'Emplacement',
      subtitle: 'Trouvez-nous sur la carte',
      loadMap: 'Cliquez pour charger Google Maps',
      mapPrivacyNotice: 'Le chargement de la carte transmettra des données à Google'
    },
    rating: {
      title: "Comment s'est passée la réservation ?",
      hint: 'Facultatif. Cela nous aide à simplifier la réservation.',
      starLabel: '{n} étoile(s) sur 5',
      improveTitle: 'Que pourrait-on améliorer ?',
      reasonAppointment: 'Trouver un créneau',
      reasonService: 'Choisir un service',
      reasonTherapist: 'Choisir un(e) thérapeute',
      reasonConfusing: "La page n'était pas claire",
      reasonSlow: 'Cela a pris trop de temps',
      reasonOther: 'Autre chose',
      commentPlaceholder: 'Dites-nous en un peu plus (facultatif)',
      submit: 'Envoyer',
      skip: 'Passer',
      thanks: 'Merci, cela nous aide vraiment.'
    }
  },
  sk: {
    nav: {
      home: 'Domov',
      offers: 'Ponuka',
      services: 'Rezervácia',
      atmosphere: 'Galéria',
      location: 'Poloha',
      massages: 'Masáže',
      visitUs: 'Navštívte nás',
      faq: 'FAQ'
    },
    services: {
      title: 'Skontrolujte dostupnosť — kliknite sem!',
      duration: 'Trvanie',
      startingFrom: 'Od',
      minutes: 'Minút',
      bookNow: 'Rezervovať',
      loading: 'Načítavajú sa služby...',
      popular: 'Populárne',
      pairBadge: 'Pre dvoch',
      more: 'Viac',
      less: 'Menej'
    },
    atmosphere: {
      title: 'Galéria',
      subtitle: 'Zažite naše relaxačné prostredie'
    },
    location: {
      title: 'Poloha',
      subtitle: 'Nájdete nás na mape'
    },
    modal: {
      title: 'Vyberte si typ masáže',
      type1: 'Tradičná thajská masáž',
      type2: 'Thajská olejová masáž',
      type3: 'Thajská aromaterapeutická masáž',
      close: 'Zavrieť',
      dateTimeTitle: 'Vyberte dátum a čas',
      selectDate: 'Vyberte dátum',
      selectTime: 'Vyberte čas',
      firstName: 'Meno',
      lastName: 'Priezvisko',
      phone: 'Telefónne číslo',
      email: 'Emailová adresa',
      emailFollowUpNotice: '{studio} Vám môže po Vašej návšteve poslať e-mail so žiadosťou o spätnú väzbu a ponukou nových rezervácií. Z odberu sa môžete kedykoľvek odhlásiť.',
      persons: 'Počet osôb',
      back: 'Späť',
      confirm: 'Rezervovať',
      termsAcceptance: 'Prečítal/a som si a súhlasím s Obchodnými podmienkami a právom na odstúpenie.',
      totalPrice: 'Celková cena',
      sum: 'Cena',
      total: 'Celkom',
      specialWish: 'Špeciálne priania / Poznámky',
      specialWishPlaceholder: 'Špeciálne požiadavky alebo poznámky k vašej návšteve... (voliteľné)',
      therapist: 'Terapeut',
      therapist1: 'Terapeut 1',
      therapist2: 'Terapeut 2',
      therapistNoPreference: 'Bez preferencie',
      therapistUnavailable: 'Tento terapeut je v zvolenom čase už rezervovaný. Prosím vyberte iný čas alebo terapeuta.',
      sameDayLeadTime: "Rezervácie na dnes je potrebné urobiť aspoň {minutes} minút vopred. Vyberte prosím neskorší čas.",
      therapistTimeFiltered: 'Váš predtým vybraný čas nie je dostupný u tohto terapeuta. Vyberte si prosím dostupný časový slot z mriežky vyššie.',
      maxPersonsHint: 'Ak si prajete rezervovať pre viac ako 2 osoby, vykonajte prosím samostatnú rezerváciu.',
      paymentTitle: 'Vyberte spôsob platby',
      paymentMethod: 'Ako chcete platiť?',
      payFull: 'Zaplatiť v plnej výške ({amount})',
      payDeposit: 'Zaplatiť zálohu teraz ({amount}), zvyšok na mieste',
      payCash: 'Zaplatiť v hotovosti na mieste',
      cardDetails: 'Údaje karty',
      confirmAndPay: 'Potvrdiť a zaplatiť {amount}',
      paymentProcessing: 'Spracovávanie platby...',
      paymentError: 'Platba zlyhala. Skúste to znova.',
      securePayment: 'Bezpečná platba powered by Stripe'
    },
    notification: {
      close: 'Zavrieť',
      bookingConfirmed: 'Rezervácia potvrdená!',
      confirmationEmail: 'Potvrdenie rezervácie bude zaslané na váš email',
      selectDateFirst: 'Najprv si vyberte dátum, aby sa zobrazili dostupné časy',
      loadingTimeSlots: 'Načítavajú sa dostupné časy...',
      noAvailableTimes: 'Pre tento dátum nie sú dostupné žiadne časy. Vyberte prosím iný dátum.',
      bookingFailed: 'Rezervácia zlyhala',
      checkInformation: 'Skontrolujte svoje údaje a skúste to znova',
      networkError: 'Chyba siete',
      checkConnection: 'Skontrolujte svoje internetové pripojenie a skúste to znova',
      date: 'Dátum',
      time: 'Čas',
      massageType: 'Typ Masáže',
      duration: 'Trvanie',
      amountPaid: 'Zaplatená Suma',
      remainingAtVenue: 'Zostatok na mieste',
      refundPolicy: 'Politika Vrátenia',
      cancellationPolicy: 'Storno podmienky',
      noRefunds: 'Žiadne vrátenie peňazí',
      refundWindow: 'Zrušenie je možné do {hours} hodín pred vaším termínom.',
      cancellationFee: 'Bude účtovaný stornopoplatok €5.',
      lateCancellationPolicy: 'Ak by vám niečo prekazilo plány a nemohli by ste na termín prísť, prosíme, dajte nám vedieť aspoň {hours} hodín vopred, a to výhradne telefonicky.',
      timeRemaining: 'Začiatok za'
    },
    hero: {
      voucherBtn: 'Kúpiť Poukaz',
      openUntil: 'Dnes otvorené do {time}',
      opensAt: 'Dnes otvárame o {time}',
      closedToday: 'Dnes zatvorené'
    },
    cta: {
      fromPrice: 'od {price} · {minutes} min'
    },
    trust: {
      sameDay: 'Termíny dostupné aj v ten istý deň',
      cancelFree: 'Bezplatné zrušenie až do {hours} h vopred',
      payOnline: 'Bezpečná online platba',
      payAtVenue: 'Zaplatíte až na mieste'
    },
    footer: {
      impressum: 'Impresum',
      dataProtection: 'Ochrana osobných údajov',
      terms: 'Obchodné podmienky',
      address: 'Adresa',
      openingHours: 'Otváracie hodiny',
      email: 'Email',
      rights: 'Všetky práva vyhradené.',
      poweredBy: 'powered by'
    },
    common: {
      closed: 'Zatvorené',
      language: 'Jazyk',
      expand: 'Rozbaliť',
      collapse: 'Zbaliť',
      previousImage: 'Predchádzajúci obrázok',
      nextImage: 'Nasledujúci obrázok',
      imageViewer: 'Prehliadač obrázkov',
      loading: 'Načítava sa…',
      min: 'min'
    },
    validation: {
      phoneInvalid: 'Telefónne číslo musí začínať znakom + alebo číslicou.',
      emailInvalid: 'Zadajte platnú e-mailovú adresu.',
      notesInvalid: 'Znaky @ \' $ # nie sú povolené.',
      required: 'Toto pole je povinné.'
    },
    booking: {
      processing: 'Spracováva sa…',
      calculating: 'Prepočítava sa…',
      datePlaceholder: 'Vyberte dátum',
      loadError: 'Nepodarilo sa načítať aktuálne údaje. Skúste to prosím znova.',
      slotsLoadError: 'Nepodarilo sa načítať dostupné časy. Skúste to prosím znova.',
      typeUnavailableOnDay: '{type} v tento deň nie je k dispozícii. Vyberte si prosím iný deň alebo inú masáž.',
      timeNoLongerAvailable: 'Tento čas už nie je k dispozícii. Vyberte si prosím iný čas.',
      chooseType: 'Vyberte si masáž',
      chooseTime: 'Vyberte si čas',
      yourDetails: 'Vaše údaje',
      confirmAndPay: 'Potvrdiť a zaplatiť',
      confirmBooking: 'Potvrdiť rezerváciu',
      stepOf: 'Krok {current} z {total}',
      change: 'Zmeniť',
      continue: 'Pokračovať',
      moreDates: 'Ďalšie dátumy',
      fewerDates: 'Menej dátumov',
      morning: 'Ráno',
      afternoon: 'Popoludnie',
      evening: 'Večer',
      slotsLeft: 'Zostáva {n}',
      anyTherapist: 'Ktorákoľvek terapeutka',
      confirmAmount: 'Potvrdiť · {amount}',
      payAmount: 'Zaplatiť {amount}',
      retry: 'Skúsiť znova',
      done: 'Hotovo',
      duration: 'Trvanie',
      selectDuration: 'Vyberte trvanie',
      durationTimeFiltered: 'Zvolený čas nie je pre toto trvanie dostupný. Vyberte prosím iný čas.',
      pairMassageHint: 'Párová masáž – rezervácia pre 2 osoby',
      priceForTwo: 'pre 2 osoby',
      priceBase: 'Základná cena',
      priceSurcharge: 'Príplatok',
      priceDiscount: 'Zľava',
      priceDetails: 'Podrobnosti',
      priceBreakdown: 'Rozpis ceny',
      prefilledHint: 'Vaše údaje sme doplnili z vašej poslednej rezervácie.',
      prefilledClear: 'Nie ste to vy? Vymazať'
    },
    visit: {
      loadingMap: 'Načítava sa mapa…',
      title: 'Navštívte nás',
      openInMaps: 'Otvoriť v Google Maps',
      call: 'Zavolať',
      today: 'Dnes'
    },
    days: {
      monday: 'Pondelok',
      tuesday: 'Utorok',
      wednesday: 'Streda',
      thursday: 'Štvrtok',
      friday: 'Piatok',
      saturday: 'Sobota',
      sunday: 'Nedeľa'
    },
    offers: {
      title: 'Naše Masáže',
      loading: 'Načítavajú sa ponuky...',
      popular: 'Populárne',
      bookFor: 'Rezervovať na:',
      selectDuration: 'Vyberte trvanie',
      priceAdjustment: 'Úprava ceny',
      noDescription: 'Popis nie je k dispozícii.',
      traditional: {
        title: 'Tradičná thajská masáž',
        description: 'Hĺbková tlaková masáž, ktorá pomocou rúk, lakťov a chodidiel naťahuje a uvoľňuje svaly. Zvyčajne sa vykonáva na podložke v oblečení.'
      },
      oil: {
        title: 'Thajská olejová masáž',
        description: 'Kombinuje jemné thajské techniky s plynulými olejovými ťahmi na uvoľnenie svalov, zlepšenie cirkulácie a výživu pokožky.'
      },
      aroma: {
        title: 'Thajská aromaterapeutická masáž',
        description: 'Používa aromatické esenciálne oleje spolu s thajskými masážnymi technikami na uvoľnenie tela a mysle a zlepšenie nálady a pohody.'
      }
    },
    faq: {
      title: 'Thajská masáž – Často kladené otázky',
      showAll: 'Zobraziť všetkých {count} otázok',
      showLess: 'Zobraziť menej',
      cat1: 'Čo je thajská masáž?',
      cat2: 'Prečo je thajská masáž iná',
      cat3: 'Čo očakávať',
      cat4: 'Prečo zákazníci vyberajú thajskú masáž',
      cat5: 'Pred vašou návštevou',
      cat6: 'Rezervácia & Zážitok',
      cat7: 'Výsledky & Benefity',
      q1: 'Čo je thajská masáž?',
      a1: 'Thajská masáž je tradičná terapia celého tela, ktorá kombinuje strečing, akupresúru a rytmické pohyby. Pomáha uvoľniť napätie, zlepšiť flexibilitu a obnoviť energetickú rovnováhu — je zároveň relaxačná aj revitalizačná.',
      q2: 'Ako sa thajská masáž líši od olejovej alebo spa masáže?',
      a2: 'Na rozdiel od olejových alebo spa masáží je thajská masáž aktívnejšia a pohybovo orientovaná. Vykonáva sa v plnom oblečení na pohodlnej podložke a zahŕňa asistovaný strečing, podobný jóge.',
      q3: 'Ako sa thajská masáž líši od masáže hlbokých tkanív?',
      a3: 'Masáž hlbokých tkanív sa zameriava na silný tlak na konkrétne stiahnuté svaly. Thajská masáž pracuje na celom tele a poskytuje komplexný reset tela.',
      q4: 'Ako sa thajská masáž líši od športovej masáže?',
      a4: 'Športová masáž je určená pre atlétov. Thajská masáž je vhodná pre všetkých a zameriava sa na zníženie stuhnutosti a úľavu od každodenného napätia.',
      q5: 'Ako sa thajská masáž cíti?',
      a5: 'Zažijete kombináciu jemného strečingu, pevného tlaku a plynulých pohybov. Mnohí hostia ju opisujú ako hlboko relaxačnú a zároveň povzbudzujúcu.',
      q6: 'Je thajská masáž bolestivá?',
      a6: 'Nie — nikdy by nemala byť bolestivá. Tlak a strečing sú vždy prispôsobené vašej úrovni komfortu. Kedykoľvek môžete požiadať o inú intenzitu.',
      q7: 'Budem sa po jednom sedení cítiť lepšie?',
      a7: 'Väčšina hostí cíti okamžitú úľavu od napätia, zlepšenú flexibilitu a ľahšie telo už po jednom sedení.',
      q8: 'Prečo by som mal vybrať thajskú masáž namiesto normálnej relaxačnej masáže?',
      a8: 'Thajská masáž ide nad rámec relaxácie. Pomáha uvoľniť hlboké stuhnutie, zlepšuje pohyblivosť a obnovuje tok energie v celom tele.',
      q9: 'Potrebujem sa nejako pripraviť pred termínom?',
      a9: 'Žiadna príprava nie je potrebná. Stačí prísť niekoľko minút skôr a obliecť si pohodlné oblečenie.',
      q10: 'Potrebujem predchádzajúce skúsenosti?',
      a10: 'Vôbec nie. Thajská masáž je vhodná pre začiatočníkov a prvotných návštevníkov.',
      q11: 'Ako si môžem rezervovať termín?',
      a11: 'Môžete si rezervovať online za niekoľko sekúnd alebo zavolať priamo do salóna. Walk-in návštevy sú tiež vítané.',
      q12: 'Môžem si vybrať úroveň tlaku?',
      a12: 'Áno. Môžete si vybrať ľahký, stredný alebo silný tlak podľa vašich preferencií.',
      q13: 'Môžem prísť s priateľom alebo partnerom?',
      a13: 'Áno, mnohé prevádzky ponúkajú párové ošetrenia v závislosti od dostupnosti miestností.',
      q14: 'Aké sú hlavné výhody thajskej masáže?',
      a14: 'Thajská masáž môže pomôcť znížiť stres, zlepšiť flexibilitu, zvýšiť cirkuláciu a uľaviť od svalovej stuhnutosti.',
      q15: 'Ako často by som mal absolvovať thajskú masáž?',
      a15: 'Mnohí klienti rezervujú raz týždenne alebo každé dva týždne, aby si udržali flexibilitu a predišli hromadeniu napätia.',
      ctaTitle: 'Ste pripravení cítiť sa lepšie?',
      ctaBody: 'Rezervujte si thajskú masáž teraz a užívajte si kompletný reset tela, ktorý vás nechá uvoľnených, flexibilných a plných energie.',
      ctaBullet1: 'Termíny v ten istý deň často dostupné',
      ctaBullet2: 'Walk-in návštevy vítané (podľa dostupnosti)',
    },
    cancellation: {
      title: 'Zrušiť rezerváciu',
      loading: 'Načítavajú sa detaily rezervácie...',
      error: 'Nepodarilo sa načítať rezerváciu',
      close: 'Zavrieť',
      bookingDetails: 'Detaily rezervácie',
      bookingId: 'ID rezervácie',
      customer: 'Zákazník',
      service: 'Služba',
      massageType: 'Typ masáže',
      date: 'Dátum',
      time: 'Čas',
      persons: 'Osoby',
      totalPrice: 'Celková cena',
      warningTitle: 'Zrušenie na poslednú chvíľu',
      warningMessage: 'Vaša rezervácia je o menej než {hours} hodín. Neskoré zrušenie môže byť spoplatnené.',
      hours: 'hodín',
      confirmTitle: 'Potvrdiť zrušenie',
      reasonLabel: 'Dôvod zrušenia (nepovinné)',
      reasonPlaceholder: 'Dajte nám prosím vedieť dôvod zrušenia...',
      processing: 'Spracováva sa...',
      confirmCancel: 'Áno, zrušiť rezerváciu',
      keepBooking: 'Ponechať rezerváciu',
      successTitle: 'Rezervácia zrušená',
      successMessage: 'Vaša rezervácia bola úspešne zrušená.',
      invalidLink: 'Neplatný odkaz na zrušenie',
      cannotCancel: 'Túto rezerváciu nie je možné zrušiť',
      confirmPrompt: 'Ste si istý/á, že chcete zrušiť túto rezerváciu? Túto akciu nie je možné vrátiť späť.',
      errorMessage: 'Zrušenie rezervácie zlyhalo. Skúste to znova alebo nás kontaktujte.',
      refundNoticeTitle: 'Bude vydaná refundácia',
      refundNoticeBody: 'Refundácia vo výške {amount} bude spracovaná (stornopoplatok €5 sa uplatní).',
      outsideWindowTitle: 'Lehota na zrušenie vypršala',
      outsideWindowBody: 'Kontaktujte štúdio priamo pre získanie refundácie.',
      contactStudioInstruction: 'Pre požiadanie o refundáciu kontaktujte štúdio priamo.',
      successWithRefund: 'Váš termín bol zrušený. Refundácia vo výške {amount} bude spracovaná do 5–10 pracovných dní.',
      successContactStudio: 'Váš termín bol zrušený. Kontaktujte štúdio priamo pre dohodnutie refundácie.',
      verifyEmail: 'Vaša emailová adresa',
      verifyEmailPlaceholder: 'Zadajte email použitý pri rezervácii',
      lateBlockedTitle: 'Online zrušenie už nie je možné',
      lateBlockedText: 'Menej ako {hours} hodín pred termínom nie je možné zrušiť rezerváciu online. Ak ju chcete zrušiť, zavolajte do štúdia.',
      lateBlockedNoPhone: 'Menej ako {hours} hodín pred termínom nie je možné zrušiť rezerváciu online. Ak ju chcete zrušiť, kontaktujte priamo štúdio.',
      hoursRemaining: 'Zostáva {hours} h',
      callStudio: 'Zavolať do štúdia'
    },
    announcement: {
      close: 'Zavrieť',
      previous: 'Predchádzajúce',
      next: 'Nasledujúce',
      noshow: 'Dnes už nezobrazovať'
    },
    offline: {
      title: 'Dočasne zatvorené',
      body: 'Online rezervácia momentálne nie je k dispozícii. Skúste to prosím neskôr.',
      bookingRefused: 'Toto štúdio momentálne neprijíma online rezervácie. Obnovte prosím stránku.'
    },
    loadLocation: {
      title: 'Poloha',
      subtitle: 'Nájdete nás na mape',
      loadMap: 'Kliknite pre načítanie Google Maps',
      mapPrivacyNotice: 'Načítanie mapy odošle údaje spoločnosti Google'
    },
    rating: {
      title: 'Ako prebehla rezervácia?',
      hint: 'Nepovinné. Pomáha nám rezerváciu zjednodušiť.',
      starLabel: '{n} z 5 hviezdičiek',
      improveTitle: 'Čo by mohlo byť lepšie?',
      reasonAppointment: 'Nájsť voľný termín',
      reasonService: 'Vybrať službu',
      reasonTherapist: 'Vybrať terapeuta',
      reasonConfusing: 'Stránka bola neprehľadná',
      reasonSlow: 'Trvalo to príliš dlho',
      reasonOther: 'Niečo iné',
      commentPlaceholder: 'Napíšte nám viac (nepovinné)',
      submit: 'Odoslať',
      skip: 'Preskočiť',
      thanks: 'Ďakujeme, naozaj nám to pomáha.'
    }
  },
  pt: {
    nav: {
      home: 'Início',
      offers: 'Ofertas',
      services: 'Reserva',
      atmosphere: 'Atmosfera',
      location: 'Localização',
      massages: 'Massagens',
      visitUs: 'Visite-nos',
      faq: 'FAQ'
    },
    services: {
      title: 'Verifique a disponibilidade — clique aqui!',
      duration: 'Duração',
      startingFrom: 'A partir de',
      minutes: 'Minutos',
      bookNow: 'Reservar agora',
      loading: 'A carregar serviços...',
      popular: 'Popular',
      pairBadge: 'Para dois',
      more: 'Mais',
      less: 'Menos'
    },
    atmosphere: {
      title: 'Atmosfera',
      subtitle: 'Descubra o nosso ambiente relaxante'
    },
    location: {
      title: 'Localização',
      subtitle: 'Encontre-nos no mapa'
    },
    modal: {
      title: 'Escolha o Seu Tipo de Massagem',
      type1: 'Massagem Tailandesa Tradicional',
      type2: 'Massagem Tailandesa com Óleo',
      type3: 'Massagem Tailandesa Aromática',
      close: 'Fechar',
      dateTimeTitle: 'Selecione Data e Hora',
      selectDate: 'Selecionar Data',
      selectTime: 'Selecionar Hora',
      firstName: 'Primeiro Nome',
      lastName: 'Apelido',
      phone: 'Número de Telefone',
      email: 'Endereço de Email',
      emailFollowUpNotice: '{studio} poderá enviar-lhe um email após a sua visita para lhe pedir a sua opinião e propor novas marcações. Pode cancelar a subscrição a qualquer momento.',
      persons: 'Número de Pessoas',
      back: 'Voltar',
      confirm: 'Reservar agora',
      termsAcceptance: 'Li e aceito os Termos e Condições e o Direito de Arrependimento.',
      totalPrice: 'Preço total',
      sum: 'Soma',
      total: 'Total',
      specialWish: 'Desejos especiais / Notas',
      specialWishPlaceholder: 'Pedidos especiais ou notas para a sua visita... (opcional)',
      therapist: 'Terapeuta',
      therapist1: 'Terapeuta 1',
      therapist2: 'Terapeuta 2',
      therapistNoPreference: 'Sem preferência',
      therapistUnavailable: 'Este terapeuta já está reservado no horário selecionado. Por favor escolha um horário ou terapeuta diferente.',
      sameDayLeadTime: "As marcações para hoje precisam de pelo menos {minutes} minutos de antecedência. Por favor, escolha um horário mais tarde.",
      therapistTimeFiltered: 'O horário selecionado anteriormente não está disponível com este terapeuta. Por favor, escolha um horário disponível na grade acima.',
      maxPersonsHint: 'Se deseja reservar para mais de 2 pessoas, por favor faça uma reserva separada.',
      paymentTitle: 'Escolher método de pagamento',
      paymentMethod: 'Como gostaria de pagar?',
      payFull: 'Pagar na totalidade ({amount})',
      payDeposit: 'Pagar sinal agora ({amount}), resto no local',
      payCash: 'Pagar em dinheiro no local',
      cardDetails: 'Dados do cartão',
      confirmAndPay: 'Confirmar e pagar {amount}',
      paymentProcessing: 'A processar pagamento...',
      paymentError: 'Pagamento falhou. Por favor tente novamente.',
      securePayment: 'Pagamento seguro powered by Stripe'
    },
    notification: {
      close: 'Fechar',
      bookingConfirmed: 'Reserva Confirmada!',
      confirmationEmail: 'A confirmação da reserva será enviada para o seu email',
      selectDateFirst: 'Por favor selecione uma data para ver os horários disponíveis',
      loadingTimeSlots: 'A carregar horários disponíveis...',
      noAvailableTimes: 'Sem horários disponíveis para esta data. Por favor selecione outra data.',
      bookingFailed: 'Reserva Falhada',
      checkInformation: 'Por favor verifique as suas informações e tente novamente',
      networkError: 'Erro de Rede',
      checkConnection: 'Por favor verifique a sua ligação e tente novamente',
      date: 'Data',
      time: 'Hora',
      massageType: 'Tipo de Massagem',
      duration: 'Duração',
      amountPaid: 'Valor Pago',
      remainingAtVenue: 'Restante no local',
      refundPolicy: 'Política de Reembolso',
      cancellationPolicy: 'Política de cancelamento',
      noRefunds: 'Sem reembolsos',
      refundWindow: 'Cancelamentos aceitos até {hours} horas antes do seu agendamento.',
      cancellationFee: 'Uma taxa de cancelamento de €5 será aplicada.',
      lateCancellationPolicy: 'Se surgir algum imprevisto e não puder comparecer ao seu agendamento, pedimos que nos avise com pelo menos {hours} horas de antecedência, exclusivamente por telefone.',
      timeRemaining: 'Começa em'
    },
    hero: {
      voucherBtn: 'Comprar Voucher',
      openUntil: 'Aberto hoje até às {time}',
      opensAt: 'Abre hoje às {time}',
      closedToday: 'Fechado hoje'
    },
    cta: {
      fromPrice: 'a partir de {price} · {minutes} min'
    },
    trust: {
      sameDay: 'Marcações disponíveis no próprio dia',
      cancelFree: 'Cancelamento gratuito até {hours} h antes',
      payOnline: 'Pagamento online seguro',
      payAtVenue: 'Pague apenas na marcação'
    },
    footer: {
      impressum: 'Informações Legais',
      dataProtection: 'Proteção de Dados',
      terms: 'Termos e Condições',
      address: 'Morada',
      openingHours: 'Horário de Funcionamento',
      email: 'Email',
      rights: 'Todos os direitos reservados.',
      poweredBy: 'powered by'
    },
    common: {
      closed: 'Fechado',
      language: 'Idioma',
      expand: 'Expandir',
      collapse: 'Recolher',
      previousImage: 'Imagem anterior',
      nextImage: 'Imagem seguinte',
      imageViewer: 'Visualizador de imagens',
      loading: 'A carregar…',
      min: 'min'
    },
    validation: {
      phoneInvalid: 'O número de telefone deve começar com + ou com um dígito.',
      emailInvalid: 'Introduza um endereço de email válido.',
      notesInvalid: 'Os caracteres @ \' $ # não são permitidos.',
      required: 'Este campo é obrigatório.'
    },
    booking: {
      processing: 'A processar…',
      calculating: 'A calcular…',
      datePlaceholder: 'Selecione uma data',
      loadError: 'Não foi possível carregar os dados mais recentes. Tente novamente.',
      slotsLoadError: 'Não foi possível carregar os horários disponíveis. Tente novamente.',
      typeUnavailableOnDay: '{type} não está disponível neste dia. Por favor escolha outro dia ou outra massagem.',
      timeNoLongerAvailable: 'Este horário já não está disponível. Por favor escolha outro horário.',
      chooseType: 'Escolha a sua massagem',
      chooseTime: 'Escolha um horário',
      yourDetails: 'Os seus dados',
      confirmAndPay: 'Confirmar e pagar',
      confirmBooking: 'Confirmar reserva',
      stepOf: 'Passo {current} de {total}',
      change: 'Alterar',
      continue: 'Continuar',
      moreDates: 'Mais datas',
      fewerDates: 'Menos datas',
      morning: 'Manhã',
      afternoon: 'Tarde',
      evening: 'Noite',
      slotsLeft: 'Restam {n}',
      anyTherapist: 'Qualquer terapeuta',
      confirmAmount: 'Confirmar · {amount}',
      payAmount: 'Pagar {amount}',
      retry: 'Tentar novamente',
      done: 'Concluído',
      duration: 'Duração',
      selectDuration: 'Escolher duração',
      durationTimeFiltered: 'O horário escolhido não está disponível para esta duração. Escolha outro horário.',
      pairMassageHint: 'Massagem a dois – reserva para 2 pessoas',
      priceForTwo: 'para 2 pessoas',
      priceBase: 'Preço base',
      priceSurcharge: 'Suplemento',
      priceDiscount: 'Desconto',
      priceDetails: 'Detalhes',
      priceBreakdown: 'Detalhe do preço',
      prefilledHint: 'Preenchemos os seus dados a partir da sua última reserva.',
      prefilledClear: 'Não é você? Limpar'
    },
    visit: {
      loadingMap: 'A carregar o mapa…',
      title: 'Visite-nos',
      openInMaps: 'Abrir no Google Maps',
      call: 'Ligar',
      today: 'Hoje'
    },
    days: {
      monday: 'Segunda-feira',
      tuesday: 'Terça-feira',
      wednesday: 'Quarta-feira',
      thursday: 'Quinta-feira',
      friday: 'Sexta-feira',
      saturday: 'Sábado',
      sunday: 'Domingo'
    },
    offers: {
      title: 'As Nossas Massagens',
      loading: 'A carregar ofertas...',
      popular: 'Popular',
      bookFor: 'Reservar para:',
      selectDuration: 'Selecionar duração',
      priceAdjustment: 'Ajuste de preço',
      noDescription: 'Sem descrição disponível.',
      traditional: {
        title: 'Massagem Tailandesa Tradicional',
        description: 'Uma massagem profunda baseada em pressão que alonga e solta os músculos usando mãos, cotovelos e pés. Frequentemente realizada num colchão de chao com roupa vestida.'
      },
      oil: {
        title: 'Massagem Tailandesa com Óleo',
        description: 'Combina técnicas tailandesas suaves com movimentos fluidos de óleo para relaxar músculos, melhorar a circulação e nutrir a pele.'
      },
      aroma: {
        title: 'Massagem Tailandesa Aromática',
        description: 'Utiliza óleos essenciais aromáticos juntamente com técnicas de massagem tailandesa para relaxar o corpo e mente enquanto melhora o humor e o bem-estar.'
      }
    },
    faq: {
      title: 'Massagem Tailandesa – Perguntas Frequentes',
      showAll: 'Ver todas as {count} perguntas',
      showLess: 'Ver menos',
      cat1: 'O que é a Massagem Tailandesa?',
      cat2: 'Por que a Massagem Tailandesa é Diferente',
      cat3: 'O que Esperar',
      cat4: 'Por que os Clientes Escolhem a Massagem Tailandesa',
      cat5: 'Antes da sua Visita',
      cat6: 'Reserva & Experiência',
      cat7: 'Resultados & Benefícios',
      q1: 'O que é a massagem tailandesa?',
      a1: 'A massagem tailandesa é uma terapia corporal tradicional que combina alongamento, acupressão e movimentos rítmicos. Ajuda a libertar a tensão, melhorar a flexibilidade e restaurar o equilíbrio energético — tornando-a tanto relaxante como revitalizante.',
      q2: 'Como é que a massagem tailandesa difere de uma massagem com óleo ou spa?',
      a2: 'Ao contrário das massagens com óleo ou spa, a massagem tailandesa é mais ativa e baseada no movimento. É realizada completamente vestida num tapete confortável e inclui alongamentos assistidos, semelhantes ao yoga.',
      q3: 'Como é que a massagem tailandesa difere da massagem de tecidos profundos?',
      a3: 'A massagem de tecidos profundos concentra-se em forte pressão em músculos específicos. A massagem tailandesa trabalha em todo o corpo, proporcionando um reset completo.',
      q4: 'Como é que a massagem tailandesa difere da massagem desportiva?',
      a4: 'A massagem desportiva é concebida para atletas. A massagem tailandesa é adequada para todos e concentra-se na redução da rigidez e alívio das tensões diárias.',
      q5: 'Como é que a massagem tailandesa se sente?',
      a5: 'Irá experienciar uma combinação de alongamentos suaves, pressão firme e movimentos fluidos. Muitos clientes descrevem-na como profundamente relaxante e energizante ao mesmo tempo.',
      q6: 'A massagem tailandesa dói?',
      a6: 'Não — nunca deve ser dolorosa. A pressão e o alongamento são sempre ajustados ao seu nível de conforto. Pode pedir uma intensidade diferente a qualquer momento.',
      q7: 'Vou sentir-me melhor após uma sessão?',
      a7: 'A maioria dos clientes sente alívio imediato da tensão, flexibilidade melhorada e um corpo mais leve e relaxado após apenas uma sessão.',
      q8: 'Por que devo escolher a massagem tailandesa em vez de uma massagem de relaxamento normal?',
      a8: 'A massagem tailandesa vai além do relaxamento. Ajuda a libertar a rigidez profunda, melhora a mobilidade e restaura o fluxo de energia por todo o corpo.',
      q9: 'Preciso de preparar alguma coisa antes da minha consulta?',
      a9: 'Não é necessária qualquer preparação. Basta chegar alguns minutos mais cedo e usar roupa confortável.',
      q10: 'Preciso de experiência prévia?',
      a10: 'De forma alguma. A massagem tailandesa é adequada para principiantes e visitantes de primeira vez.',
      q11: 'Como posso reservar uma consulta?',
      a11: 'Pode reservar online em apenas alguns segundos ou ligar diretamente para a loja. Walk-ins são também bem-vindos.',
      q12: 'Posso escolher o nível de pressão?',
      a12: 'Sim. Pode escolher pressão leve, média ou forte com base na sua preferência.',
      q13: 'Posso vir com um amigo ou parceiro?',
      a13: 'Sim, muitos locais oferecem tratamentos em casal dependendo da disponibilidade de sala.',
      q14: 'Quais são os principais benefícios da massagem tailandesa?',
      a14: 'A massagem tailandesa pode ajudar a reduzir o stress, melhorar a flexibilidade, aumentar a circulação e aliviar a rigidez muscular.',
      q15: 'Com que frequência devo fazer uma massagem tailandesa?',
      a15: 'Muitos clientes reservam uma vez por semana ou de duas em duas semanas para manter a flexibilidade e prevenir o acúmulo de tensão.',
      ctaTitle: 'Pronto para se sentir melhor?',
      ctaBody: 'Reserve já a sua massagem tailandesa e desfrute de um reset completo do corpo que o deixa relaxado, flexível e recarregado.',
      ctaBullet1: 'Consultas no mesmo dia frequentemente disponíveis',
      ctaBullet2: 'Walk-ins bem-vindos (sujeito a disponibilidade)',
    },
    cancellation: {
      title: 'Cancelar Consulta',
      loading: 'A carregar detalhes da reserva...',
      error: 'Não foi possível carregar a reserva',
      close: 'Fechar',
      bookingDetails: 'Detalhes da Reserva',
      bookingId: 'ID da Reserva',
      customer: 'Cliente',
      service: 'Serviço',
      massageType: 'Tipo de Massagem',
      date: 'Data',
      time: 'Hora',
      persons: 'Pessoas',
      totalPrice: 'Preço Total',
      warningTitle: 'Cancelamento de Última Hora',
      warningMessage: 'A sua consulta é em menos de {hours} horas. Cancelamentos tardios podem estar sujeitos a taxas.',
      hours: 'horas',
      confirmTitle: 'Confirmar Cancelamento',
      reasonLabel: 'Motivo do cancelamento (opcional)',
      reasonPlaceholder: 'Por favor diga-nos porque precisa de cancelar...',
      processing: 'A processar...',
      confirmCancel: 'Sim, Cancelar Consulta',
      keepBooking: 'Manter a Minha Reserva',
      successTitle: 'Consulta Cancelada',
      successMessage: 'A sua consulta foi cancelada com sucesso.',
      invalidLink: 'Link de cancelamento inválido',
      cannotCancel: 'Esta reserva não pode ser cancelada',
      confirmPrompt: 'Tem a certeza que quer cancelar esta consulta? Esta ação não pode ser desfeita.',
      errorMessage: 'Falha ao cancelar a consulta. Por favor tente novamente ou contacte-nos diretamente.',
      refundNoticeTitle: 'Um reembolso será emitido',
      refundNoticeBody: 'Um reembolso de {amount} será processado (taxa de cancelamento de €5 aplicada).',
      outsideWindowTitle: 'Prazo de cancelamento expirado',
      outsideWindowBody: 'Por favor, contacte o estúdio diretamente para solicitar o seu reembolso.',
      contactStudioInstruction: 'Para solicitar um reembolso, contacte o estúdio diretamente.',
      successWithRefund: 'O seu agendamento foi cancelado. Um reembolso de {amount} será processado em 5 a 10 dias úteis.',
      successContactStudio: 'O seu agendamento foi cancelado. Contacte o estúdio diretamente para tratar do seu reembolso.',
      verifyEmail: 'O seu endereço de email',
      verifyEmailPlaceholder: 'Introduza o email utilizado na reserva',
      lateBlockedTitle: 'Cancelamento online já não é possível',
      lateBlockedText: 'Não é possível cancelar online nas {hours} horas anteriores ao seu agendamento. Por favor, ligue para o estúdio para cancelar.',
      lateBlockedNoPhone: 'Não é possível cancelar online nas {hours} horas anteriores ao seu agendamento. Por favor, contacte diretamente o estúdio para cancelar.',
      hoursRemaining: 'Faltam {hours} h',
      callStudio: 'Ligar para o estúdio'
    },
    announcement: {
      close: 'Fechar',
      previous: 'Anterior',
      next: 'Seguinte',
      noshow: 'Não mostrar hoje'
    },
    offline: {
      title: 'Temporariamente fechado',
      body: 'A reserva online está de momento indisponível. Por favor, volte mais tarde.',
      bookingRefused: 'Este estúdio não está a aceitar reservas online temporariamente. Por favor, recarregue a página.'
    },
    loadLocation: {
      title: 'Localização',
      subtitle: 'Encontre-nos no mapa',
      loadMap: 'Clique para carregar o Google Maps',
      mapPrivacyNotice: 'Carregar o mapa irá transmitir dados ao Google'
    },
    rating: {
      title: 'Como correu a reserva?',
      hint: 'Opcional. Ajuda-nos a tornar a reserva mais simples.',
      starLabel: '{n} de 5 estrelas',
      improveTitle: 'O que podia ser melhor?',
      reasonAppointment: 'Encontrar um horário',
      reasonService: 'Escolher um serviço',
      reasonTherapist: 'Escolher um/uma terapeuta',
      reasonConfusing: 'A página era confusa',
      reasonSlow: 'Demorou demasiado',
      reasonOther: 'Outra coisa',
      commentPlaceholder: 'Conte-nos um pouco mais (opcional)',
      submit: 'Enviar',
      skip: 'Saltar',
      thanks: 'Obrigado, ajuda-nos mesmo.'
    }
  },
  hu: {
    nav: {
      home: 'Főoldal',
      offers: 'Ajánlatok',
      services: 'Foglalás',
      atmosphere: 'Hangulat',
      location: 'Helyszín',
      massages: 'Masszázsok',
      visitUs: 'Kapcsolat',
      faq: 'GYIK'
    },
    services: {
      title: 'Nézze meg a szabad időpontokat – kattintson ide!',
      duration: 'Időtartam',
      startingFrom: 'Ár már',
      minutes: 'perc',
      bookNow: 'Foglalás',
      loading: 'Szolgáltatások betöltése...',
      popular: 'Népszerű',
      pairBadge: 'Két főre',
      more: 'Több',
      less: 'Kevesebb'
    },
    atmosphere: {
      title: 'Hangulat',
      subtitle: 'Ismerje meg nyugodt, pihentető környezetünket'
    },
    location: {
      title: 'Helyszín',
      subtitle: 'Keressen meg minket a térképen'
    },
    modal: {
      title: 'Válassza ki a masszázs típusát',
      type1: 'Hagyományos thai masszázs',
      type2: 'Thai olajos masszázs',
      type3: 'Thai aromaterápiás masszázs',
      close: 'Bezárás',
      dateTimeTitle: 'Dátum és időpont',
      selectDate: 'Dátum kiválasztása',
      selectTime: 'Időpont kiválasztása',
      firstName: 'Keresztnév',
      lastName: 'Vezetéknév',
      phone: 'Telefonszám',
      email: 'E-mail-cím',
      emailFollowUpNotice: '{studio} a látogatása után e-mailben kérheti ki a véleményét, és új időpontokat ajánlhat Önnek. Bármikor leiratkozhat.',
      persons: 'Személyek száma',
      back: 'Vissza',
      confirm: 'Foglalás',
      termsAcceptance: 'Elolvastam és elfogadom az Általános Szerződési Feltételeket és az elállási tájékoztatót.',
      totalPrice: 'Teljes ár',
      sum: 'Összeg',
      total: 'Összesen',
      specialWish: 'Különleges kérések / megjegyzések',
      specialWishPlaceholder: 'Különleges kérés vagy megjegyzés a látogatásához... (nem kötelező)',
      therapist: 'Terapeuta',
      therapist1: '1. terapeuta',
      therapist2: '2. terapeuta',
      therapistNoPreference: 'Mindegy',
      therapistUnavailable: 'Ez a terapeuta a kiválasztott időpontban már foglalt. Kérjük, válasszon másik időpontot vagy terapeutát.',
      sameDayLeadTime: 'Mai napra legalább {minutes} perccel előre lehet foglalni. Kérjük, válasszon későbbi időpontot.',
      therapistTimeFiltered: 'A korábban kiválasztott időpont ennél a terapeutánál nem elérhető. Kérjük, válasszon egy szabad időpontot a fenti táblázatból.',
      maxPersonsHint: 'Ha kettőnél több személyre szeretne foglalni, kérjük, adjon le külön foglalást.',
      paymentTitle: 'Fizetési mód kiválasztása',
      paymentMethod: 'Hogyan szeretne fizetni?',
      payFull: 'Teljes összeg fizetése ({amount})',
      payDeposit: 'Előleg most ({amount}), a többi a helyszínen',
      payCash: 'Készpénzes fizetés a helyszínen',
      cardDetails: 'Kártyaadatok',
      confirmAndPay: 'Megerősítés és fizetés: {amount}',
      paymentProcessing: 'Fizetés feldolgozása...',
      paymentError: 'A fizetés sikertelen. Kérjük, próbálja újra.',
      securePayment: 'Biztonságos fizetés a Stripe-on keresztül'
    },
    notification: {
      close: 'Bezárás',
      bookingConfirmed: 'Foglalás visszaigazolva!',
      confirmationEmail: 'A foglalás visszaigazolását e-mailben küldjük el Önnek',
      selectDateFirst: 'A szabad időpontok megtekintéséhez először válasszon dátumot',
      loadingTimeSlots: 'Szabad időpontok betöltése...',
      noAvailableTimes: 'Erre a napra nincs szabad időpont. Kérjük, válasszon másik dátumot.',
      bookingFailed: 'A foglalás sikertelen',
      checkInformation: 'Kérjük, ellenőrizze az adatait, és próbálja újra',
      networkError: 'Hálózati hiba',
      checkConnection: 'Kérjük, ellenőrizze az internetkapcsolatát, és próbálja újra',
      date: 'Dátum',
      time: 'Időpont',
      massageType: 'Masszázs típusa',
      duration: 'Időtartam',
      amountPaid: 'Kifizetett összeg',
      remainingAtVenue: 'Fizetendő a helyszínen',
      refundPolicy: 'Visszatérítési feltételek',
      cancellationPolicy: 'Lemondási feltételek',
      noRefunds: 'Nincs visszatérítés',
      refundWindow: 'Lemondás legkésőbb {hours} órával az időpont előtt lehetséges.',
      cancellationFee: 'A lemondási díj 5 €.',
      lateCancellationPolicy: 'Ha közbejön valami, és nem tud eljönni az időpontjára, kérjük, legalább {hours} órával előtte jelezze nekünk, kizárólag telefonon.',
      timeRemaining: 'Kezdésig hátra'
    },
    hero: {
      voucherBtn: 'Ajándékutalvány',
      openUntil: 'Ma {time}-ig nyitva',
      opensAt: 'Ma {time}-kor nyit',
      closedToday: 'Ma zárva'
    },
    cta: {
      fromPrice: '{price}-tól · {minutes} perc'
    },
    trust: {
      sameDay: 'Akár aznapi időpont',
      cancelFree: 'Ingyenes lemondás akár {hours} órával előtte',
      payOnline: 'Biztonságos online fizetés',
      payAtVenue: 'Fizetés a helyszínen'
    },
    footer: {
      impressum: 'Impresszum',
      dataProtection: 'Adatvédelem',
      terms: 'ÁSZF',
      address: 'Cím',
      openingHours: 'Nyitvatartás',
      email: 'E-mail',
      rights: 'Minden jog fenntartva.',
      poweredBy: 'powered by'
    },
    common: {
      closed: 'Zárva',
      language: 'Nyelv',
      expand: 'Kibontás',
      collapse: 'Összecsukás',
      previousImage: 'Előző kép',
      nextImage: 'Következő kép',
      imageViewer: 'Képnézegető',
      loading: 'Betöltés…',
      min: 'perc'
    },
    validation: {
      phoneInvalid: 'A telefonszámnak + jellel vagy számjeggyel kell kezdődnie.',
      emailInvalid: 'Kérjük, adjon meg érvényes e-mail-címet.',
      notesInvalid: 'A @ \' $ # karakterek nem engedélyezettek.',
      required: 'Kötelező mező.'
    },
    booking: {
      processing: 'Feldolgozás…',
      calculating: 'Számítás…',
      datePlaceholder: 'Válasszon dátumot',
      loadError: 'Nem sikerült betölteni a legfrissebb adatokat. Kérjük, próbálja újra.',
      slotsLoadError: 'Nem sikerült betölteni a szabad időpontokat. Kérjük, próbálja újra.',
      typeUnavailableOnDay: '{type} ezen a napon nem érhető el. Kérjük, válasszon másik napot vagy masszázst.',
      timeNoLongerAvailable: 'Ez az időpont már nem szabad. Kérjük, válasszon másikat.',
      chooseType: 'Válasszon masszázst',
      chooseTime: 'Válasszon időpontot',
      yourDetails: 'Az Ön adatai',
      confirmAndPay: 'Megerősítés és fizetés',
      confirmBooking: 'Foglalás megerősítése',
      stepOf: 'Lépés: {current}/{total}',
      change: 'Módosítás',
      continue: 'Tovább',
      moreDates: 'További napok',
      fewerDates: 'Kevesebb nap',
      morning: 'Délelőtt',
      afternoon: 'Délután',
      evening: 'Este',
      slotsLeft: 'Még {n} szabad',
      anyTherapist: 'Bármelyik terapeuta',
      confirmAmount: 'Megerősítés · {amount}',
      payAmount: 'Fizetés · {amount}',
      retry: 'Újra',
      done: 'Kész',
      duration: 'Időtartam',
      selectDuration: 'Időtartam kiválasztása',
      durationTimeFiltered: 'A kiválasztott időpont ennél az időtartamnál nem elérhető. Kérjük, válasszon másik időpontot.',
      pairMassageHint: 'Páros masszázs – foglalás 2 személyre',
      priceForTwo: '2 személyre',
      priceBase: 'Alapár',
      priceSurcharge: 'Felár',
      priceDiscount: 'Kedvezmény',
      priceDetails: 'Részletek',
      priceBreakdown: 'Ár részletezése',
      prefilledHint: 'Adatait a legutóbbi foglalásából töltöttük ki.',
      prefilledClear: 'Nem Ön az? Törlés'
    },
    visit: {
      loadingMap: 'Térkép betöltése…',
      title: 'Látogasson el hozzánk',
      openInMaps: 'Megnyitás a Google Mapsben',
      call: 'Hívás',
      today: 'Ma'
    },
    days: {
      monday: 'Hétfő',
      tuesday: 'Kedd',
      wednesday: 'Szerda',
      thursday: 'Csütörtök',
      friday: 'Péntek',
      saturday: 'Szombat',
      sunday: 'Vasárnap'
    },
    offers: {
      title: 'Masszázsaink',
      loading: 'Ajánlatok betöltése...',
      popular: 'Népszerű',
      bookFor: 'Foglalás:',
      selectDuration: 'Időtartam kiválasztása',
      priceAdjustment: 'Árkorrekció',
      noDescription: 'Nincs elérhető leírás.',
      traditional: {
        title: 'Hagyományos thai masszázs',
        description: 'Mély, nyomáson alapuló masszázs, amely kézzel, könyökkel és lábbal nyújtja és lazítja az izmokat. Általában matracon, felöltözve végzik.'
      },
      oil: {
        title: 'Thai olajos masszázs',
        description: 'A gyengéd thai technikákat lágy, olajos simításokkal ötvözi: ellazítja az izmokat, javítja a vérkeringést és ápolja a bőrt.'
      },
      aroma: {
        title: 'Thai aromaterápiás masszázs',
        description: 'Illatos illóolajokat használ a thai masszázstechnikák mellett, hogy ellazítsa a testet és a lelket, és javítsa a hangulatot és a közérzetet.'
      }
    },
    faq: {
      title: 'Thai masszázs – gyakran ismételt kérdések',
      showAll: 'Összes kérdés ({count})',
      showLess: 'Kevesebb',
      cat1: 'Mi a thai masszázs?',
      cat2: 'Miben más a thai masszázs?',
      cat3: 'Mire számíthat?',
      cat4: 'Miért választják a thai masszázst?',
      cat5: 'A látogatás előtt',
      cat6: 'Foglalás és élmény',
      cat7: 'Hatások és előnyök',
      q1: 'Mi a thai masszázs?',
      a1: 'A thai masszázs hagyományos, egész testre kiterjedő kezelés, amely nyújtást, akupresszúrát és ritmikus mozdulatokat ötvöz. Segít oldani a feszültséget, javítja a hajlékonyságot és helyreállítja az energiák egyensúlyát – egyszerre pihentet és felfrissít.',
      q2: 'Miben különbözik a thai masszázs az olajos vagy wellness masszázstól?',
      a2: 'Az olajos vagy wellness masszázzsal ellentétben, amelyek főként az izmok ellazítására összpontosítanak, a thai masszázs aktívabb, mozgásra épülő kezelés. Felöltözve, kényelmes matracon végzik, és a jógához hasonló, asszisztált nyújtásokat tartalmaz. Ezért ideális a hajlékonyság, a testtartás és a hosszú távú testi jólét javítására – nem csupán rövid távú ellazulásra.',
      q3: 'Miben különbözik a thai masszázs a mélyszöveti masszázstól?',
      a3: 'A mélyszöveti masszázs erős nyomást alkalmaz egyes feszes izmokra. A thai masszázs nyújtással, nyomáspontokkal és mozgással az egész testen dolgozik. Így nem csak egy-egy fájdalmas területet kezel, hanem az egész testet „újraindítja”.',
      q4: 'Miben különbözik a thai masszázs a sportmasszázstól?',
      a4: 'A sportmasszázs sportolóknak és sérülés utáni regenerációra készült. A thai masszázs mindenkinek ajánlott: csökkenti a merevséget, javítja a mozgékonyságot, és oldja a munka, a stressz vagy a hosszú ülés okozta mindennapi feszültséget.',
      q5: 'Milyen érzés a thai masszázs?',
      a5: 'Gyengéd nyújtás, határozott nyomás és folyamatos, áramló mozdulatok kombinációját fogja érezni. Sok vendégünk szerint mélyen ellazító, ugyanakkor felfrissítő élmény.',
      q6: 'Fájdalmas a thai masszázs?',
      a6: 'Nem – soha nem szabad fájdalmasnak lennie. A nyomást és a nyújtást mindig az Ön komfortérzetéhez igazítjuk. Bármikor kérhet gyengébb vagy erősebb intenzitást.',
      q7: 'Már egy kezelés után jobban fogom érezni magam?',
      a7: 'A legtöbb vendég már egyetlen kezelés után azonnali enyhülést érez: oldódik a feszültség, javul a hajlékonyság, és a test könnyebbnek, ellazultabbnak tűnik.',
      q8: 'Miért válasszam a thai masszázst egy hagyományos relaxáló masszázs helyett?',
      a8: 'A thai masszázs több mint ellazulás. Segít oldani a mély merevséget, javítja a mozgékonyságot, és helyreállítja az energia áramlását az egész testben. Különösen ajánlott, ha sokat ül, feszesek az izmai, vagy az egész testét szeretné „újraindítani”.',
      q9: 'Kell valamire készülnöm az időpont előtt?',
      a9: 'Nem szükséges semmilyen előkészület. Érkezzen néhány perccel korábban, és viseljen kényelmes ruhát.',
      q10: 'Szükséges előzetes tapasztalat?',
      a10: 'Egyáltalán nem. A thai masszázs kezdőknek és első alkalommal érkezőknek is ajánlott.',
      q11: 'Hogyan foglalhatok időpontot?',
      a11: 'Online néhány másodperc alatt foglalhat, vagy hívja fel közvetlenül az üzletet. Előzetes bejelentkezés nélkül is szívesen várjuk, ha van szabad hely.',
      q12: 'Megválaszthatom a nyomás erősségét?',
      a12: 'Igen. Igénye szerint választhat gyenge, közepes vagy erős nyomást.',
      q13: 'Jöhetek barátommal vagy a párommal?',
      a13: 'Igen, sok helyen a termek szabad kapacitásától függően páros vagy egymás melletti kezelést is kínálnak.',
      q14: 'Melyek a thai masszázs fő előnyei?',
      a14: 'A thai masszázs segíthet csökkenteni a stresszt, javítani a hajlékonyságot, fokozni a vérkeringést, és enyhíteni a mindennapok vagy a munka okozta izommerevséget.',
      q15: 'Milyen gyakran érdemes thai masszázsra járni?',
      a15: 'Sok vendégünk hetente vagy kéthetente foglal, hogy megőrizze a hajlékonyságát, csökkentse a stresszt, és megelőzze a feszültség felhalmozódását.',
      ctaTitle: 'Készen áll, hogy jobban érezze magát?',
      ctaBody: 'Foglalja le most thai masszázsát, és élvezze az egész testet megújító kezelést, amely után ellazultnak, hajlékonynak és feltöltődöttnek érzi magát.',
      ctaBullet1: 'Gyakran aznapra is van szabad időpont',
      ctaBullet2: 'Bejelentkezés nélkül is várjuk (ha van szabad hely)',
    },
    cancellation: {
      title: 'Időpont lemondása',
      loading: 'Foglalás adatainak betöltése...',
      error: 'A foglalás nem tölthető be',
      close: 'Bezárás',
      bookingDetails: 'Foglalás részletei',
      bookingId: 'Foglalási azonosító',
      customer: 'Ügyfél',
      service: 'Szolgáltatás',
      massageType: 'Masszázs típusa',
      date: 'Dátum',
      time: 'Időpont',
      persons: 'Személyek',
      totalPrice: 'Teljes ár',
      warningTitle: 'Rövid határidejű lemondás',
      warningMessage: 'Az időpontja kevesebb mint {hours} óra múlva kezdődik. A késői lemondás díjköteles lehet.',
      hours: 'óra',
      confirmTitle: 'Lemondás megerősítése',
      reasonLabel: 'A lemondás oka (nem kötelező)',
      reasonPlaceholder: 'Kérjük, írja meg, miért mondja le az időpontot...',
      processing: 'Feldolgozás...',
      confirmCancel: 'Igen, lemondom',
      keepBooking: 'Megtartom a foglalást',
      successTitle: 'Időpont lemondva',
      successMessage: 'Az időpontját sikeresen lemondtuk.',
      invalidLink: 'Érvénytelen lemondási link',
      cannotCancel: 'Ez a foglalás nem mondható le',
      confirmPrompt: 'Biztosan le szeretné mondani ezt az időpontot? A művelet nem vonható vissza.',
      errorMessage: 'Az időpontot nem sikerült lemondani. Kérjük, próbálja újra, vagy lépjen kapcsolatba velünk közvetlenül.',
      refundNoticeTitle: 'Visszatérítés',
      refundNoticeBody: '{amount} összeget visszatérítünk Önnek (az 5 € lemondási díj levonásával).',
      outsideWindowTitle: 'A lemondási határidő lejárt',
      outsideWindowBody: 'A visszatérítéshez kérjük, forduljon közvetlenül a stúdióhoz.',
      contactStudioInstruction: 'Visszatérítés igényléséhez kérjük, forduljon közvetlenül a stúdióhoz.',
      successWithRefund: 'Az időpontját lemondtuk. A visszatérítést ({amount}) 5–10 munkanapon belül teljesítjük.',
      successContactStudio: 'Az időpontját lemondtuk. A visszatérítés intézéséhez kérjük, forduljon közvetlenül a stúdióhoz.',
      verifyEmail: 'Az Ön e-mail-címe',
      verifyEmailPlaceholder: 'A foglaláskor megadott e-mail-cím',
      lateBlockedTitle: 'Az online lemondás már nem lehetséges',
      lateBlockedText: 'Az időpont előtti {hours} órában online lemondás nem lehetséges. Kérjük, a lemondáshoz hívja fel a stúdiót.',
      lateBlockedNoPhone: 'Az időpont előtti {hours} órában online lemondás nem lehetséges. Kérjük, a lemondáshoz forduljon közvetlenül a stúdióhoz.',
      hoursRemaining: 'Még {hours} óra',
      callStudio: 'Stúdió hívása'
    },
    announcement: {
      close: 'Bezárás',
      previous: 'Előző',
      next: 'Következő',
      noshow: 'Ma ne jelenjen meg újra'
    },
    offline: {
      title: 'Átmenetileg zárva',
      body: 'Az online foglalás jelenleg nem elérhető. Kérjük, nézzen vissza később.',
      bookingRefused: 'Ez a stúdió átmenetileg nem fogad online foglalást. Kérjük, töltse újra az oldalt.'
    },
    loadLocation: {
      title: 'Helyszín',
      subtitle: 'Keressen meg minket a térképen',
      loadMap: 'Kattintson a Google Maps betöltéséhez',
      mapPrivacyNotice: 'A térkép betöltésekor adatok kerülnek továbbításra a Google részére'
    },
    rating: {
      title: 'Milyen volt a foglalás?',
      hint: 'Nem kötelező. Segít, hogy egyszerűbbé tegyük a foglalást.',
      starLabel: '{n} / 5 csillag',
      improveTitle: 'Min javíthatnánk?',
      reasonAppointment: 'Időpont keresése',
      reasonService: 'Szolgáltatás kiválasztása',
      reasonTherapist: 'Terapeuta kiválasztása',
      reasonConfusing: 'Az oldal áttekinthetetlen volt',
      reasonSlow: 'Túl sokáig tartott',
      reasonOther: 'Egyéb',
      commentPlaceholder: 'Írja meg röviden, mi történt (nem kötelező)',
      submit: 'Küldés',
      skip: 'Kihagyás',
      thanks: 'Köszönjük, ez sokat segít.'
    }
  }
}

// Pre-mount guess from ?lang= and the browser. main.js re-resolves with the tenant's
// default customer language (GET /api/tenant-config) before mounting, so nothing renders
// in this locale unless the tenant default is automatic (null).
const i18n = createI18n({
  legacy: false,
  locale: resolveInitialLocale({ urlLang: readUrlLang(), browserLangs: readBrowserLangs() }),
  fallbackLocale: 'en',
  messages
})

// Set <html lang> as early as possible so crawlers see the correct language
document.documentElement.lang = i18n.global.locale.value || 'en'

export { resolveInitialLocale, SUPPORTED_LOCALES } from './utils/locale'
export default i18n
